/**
 * 真实 API 客户端（同源，统一信封 `{ data, meta }`）。
 *
 * 硬性约定：
 *   1. 所有请求带 `credentials: "same-origin"` —— 会话是 HttpOnly cookie，
 *      **前端不读、不存、不传 token**（AGENTS.md §7）。
 *   2. 成功响应解析信封；错误响应解析 `{ error: { code, message, requestId, ... } }`，
 *      并把 `code` 与 HTTP 状态**结构化**暴露（`ApiClientError`），业务侧只按 code 分支，
 *      不解析 message 文本。
 *   3. 401（非登录失败的 `INVALID_CREDENTIALS`）→ 清空内存会话并跳 `/login`：
 *      优先走注入的 SPA 处理器（`setSessionExpiredHandler`，main.tsx 注册），
 *      没有处理器时退化为 `location.assign`，保证一定会离开受保护页面。
 *   4. `null` 是「无法计算 / 未提供」，客户端**不做**任何默认值填充或指标换算。
 */

import { useAuthStore } from "../store/authStore.js";
import type { AutoMergeComboSharesData, AutoMergeSaveResult, AutoMergeSourceCampaignsData, MetaTokenOverview, MetaTokenUpdateResult, AutoMergeSourceData, AutoMergeSourceSaveInput, AutoMergeTargetsData, AutoMergeTargetSaveInput,
  ActionItemResult,
  ActionPlanResult,
  AdEditPanel,
  ActionTargetInput,
  AdEntityLevel,
  AdEntityRow,
  AdTreeCoverage,
  AdTreeCurrencyMetrics,
  AdTreeCurrencySummaryRow,
  AdTreeData,
  AdTreeHistoryData,
  AdTreeNode,
  AdTreeTotals,
  AuthUser,
  AutoControlCampaignRow,
  AutoControlExceptionRow,
  AutoControlExecutionsData,
  AutoControlPreviewData,
  AutoControlRunsData,
  AutoControlRollbackData,
  AutoControlTemplateConfig,
  AutoControlTemplateRow,
  BrandAdminRow,
  BrandStatus,
  BudgetHistoryData,
  CampaignLanguageUpsertItem,
  ComplianceAuditData,
  CampaignLanguageUpsertResult,
  CategoryNode,
  CategoryTreeData,
  CredentialHealth,
  CredentialHealthData,
  DataReadinessData,
  EntityMetricsData,
  EntityMetricsRow,
  GoogleActionPlan,
  GoogleBudgetHistoryItem,
  GoogleCampaignsData,
  GoogleOverviewData,
  MergeExecuteResult,
  MergeLinkRecordRow,
  MergePreviewData,
  MergeRequestBody,
  MetaPendingData,
  MetaActionName,
  MetricSeriesPoint,
  MetricsSummaryData,
  OperationLogEntryRow,
  PendingRetryData,
  PendingRollbackData,
  PermissionGrantDiff,
  PermissionUser,
  ProductDetail,
  ProductListItem,
  ProductListRecord,
  CreativeBoardData,
  CreativeCorrectedDrilldownData,
  CreativeMaterialAd,
  ResponseWarning,
  ShopAdminRow,
  ShopBudgetRatioListData,
  ShopBudgetRatioWriteData,
  ShopHistoryData,
  ShopMetricsData,
  GoogleMetricsData,
  ShopSyncStatusRow,
  SpendOverviewData,
  StyleCodeBoardLanguage,
  StyleCodeBoardLanguageRow,
  StyleCodeBoardRow,
  StyleCodeGroupPanel,
  StyleCodeGroupWriteRow,
  SyncPlatformStatus,
  SyncTriggerJob,
  TimezoneReportData,
  UtmCoverageSummary,

  RoleAdminRecord,
  RoleGrantInput,

  AutoBudgetBindingRecord,
  AutoBudgetTemplateConfig,
  AutoBudgetTemplateRecord,
  AudienceGeoData,
  EnrollmentRuleView,
  EnrollmentPreviewData,
  TargetRampShadowData,
  TargetRampShadowRunResult,
  TargetRampStepsData,
  TargetRampTargetRecord,
} from "./apiTypes.js";
import type { AdTreeScaleBasis, AdTreeSortDirection, AutoBudgetPreviewRow, DerivedMetrics, MetricBucket, NotDeliveringReasonKind } from "@ams/contracts";
import type { MergeDestinationsData } from "./mergeDestination.js";

// --------------------------------------------------------------------- 信封

export interface ApiMeta {
  readonly requestId: string | null;
  readonly generatedAt: string | null;
  readonly nextCursor: string | null;
  readonly warnings: readonly ResponseWarning[];
  readonly shopifyMer: number | null;
}

export interface ApiEnvelope<T> {
  readonly data: T;
  readonly meta: ApiMeta;
}

export interface ApiErrorDetail {
  readonly path: string;
  readonly issue: string;
  /** 机器码（可选；权限拒绝时 = 契约 `GrantRejectionCode`）。**只进 tooltip**，不当正文。 */
  readonly code?: string | undefined;
}

const EMPTY_META: ApiMeta = {
  requestId: null,
  generatedAt: null,
  nextCursor: null,
  warnings: [],
  shopifyMer: null,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === "string" ? value : null;
}

function readNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function parseWarnings(raw: unknown): readonly ResponseWarning[] {
  if (!Array.isArray(raw)) return [];
  const warnings: ResponseWarning[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const code = readString(item, "code");
    const message = readString(item, "message");
    if (code !== null && message !== null) warnings.push({ code, message });
  }
  return warnings;
}

export function parseMeta(raw: unknown): ApiMeta {
  if (!isRecord(raw)) return EMPTY_META;
  return {
    requestId: readString(raw, "requestId"),
    generatedAt: readString(raw, "generatedAt"),
    nextCursor: readString(raw, "nextCursor"),
    warnings: parseWarnings(raw["warnings"]),
    shopifyMer: readNumber(raw, "shopifyMer"),
  };
}

/** 解析 `{ data, meta }`；对未包信封的响应保持宽容（data = 原体）。 */
export function parseEnvelope<T>(body: unknown): ApiEnvelope<T> {
  if (isRecord(body)) {
    const meta = parseMeta(body["meta"]);
    if (Object.prototype.hasOwnProperty.call(body, "data")) {
      return { data: body["data"] as T, meta };
    }
    return { data: body as T, meta };
  }
  return { data: body as T, meta: EMPTY_META };
}

// ----------------------------------------------------------------- 结构化错误

export class ApiClientError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly requestId: string | null;
  readonly details: readonly ApiErrorDetail[];
  readonly retryAfterSeconds: number | null;

  constructor(input: {
    readonly code: string;
    readonly message: string;
    readonly httpStatus: number;
    readonly requestId?: string | null;
    readonly details?: readonly ApiErrorDetail[];
    readonly retryAfterSeconds?: number | null;
  }) {
    super(input.message);
    this.name = "ApiClientError";
    this.code = input.code;
    this.httpStatus = input.httpStatus;
    this.requestId = input.requestId ?? null;
    this.details = input.details ?? [];
    this.retryAfterSeconds = input.retryAfterSeconds ?? null;
  }

  /** 登录失败（用户名/密码错）——**不得**触发「会话过期」跳转。 */
  get isInvalidCredentials(): boolean {
    return this.code === "INVALID_CREDENTIALS";
  }

  /** 服务端未开启写操作（403）。UI 要显示成「服务端未开启写操作」而不是普通错误。 */
  get isWritesDisabled(): boolean {
    return this.code === "WRITES_DISABLED";
  }

  /** 未认证：清会话 + 跳登录。 */
  get isUnauthenticated(): boolean {
    return this.httpStatus === 401 && !this.isInvalidCredentials;
  }
}

export function parseErrorBody(httpStatus: number, body: unknown): ApiClientError {
  if (isRecord(body)) {
    const error = body["error"];
    if (isRecord(error)) {
      const rawDetails = error["details"];
      const details: ApiErrorDetail[] = [];
      if (Array.isArray(rawDetails)) {
        for (const item of rawDetails) {
          if (!isRecord(item)) continue;
          const path = readString(item, "path");
          const issue = readString(item, "issue");
          if (path !== null && issue !== null) {
            details.push({ path, issue, code: readString(item, "code") ?? undefined });
          }
        }
      }
      return new ApiClientError({
        code: readString(error, "code") ?? `HTTP_${httpStatus}`,
        message: readString(error, "message") ?? `HTTP ${httpStatus}`,
        httpStatus,
        requestId: readString(error, "requestId"),
        details,
        retryAfterSeconds: readNumber(error, "retryAfterSeconds"),
      });
    }
  }
  return new ApiClientError({
    code: `HTTP_${httpStatus}`,
    message: `请求失败（HTTP ${httpStatus}）`,
    httpStatus,
  });
}

// ------------------------------------------------------------- 会话失效处理

let sessionExpiredHandler: (() => void) | null = null;

/**
 * 注册 401（会话失效）时的处理器。main.tsx 用 SPA 路由跳转注册它；
 * 未注册时退化为整页跳转（保证不会停在受保护页面）。
 */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

export function handleUnauthenticated(): void {
  useAuthStore.getState().markUnauthenticated();
  if (sessionExpiredHandler !== null) {
    sessionExpiredHandler();
    return;
  }
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  if (pathname === "/login") return;
  window.location.assign(`/login?redirect=${encodeURIComponent(`${pathname}${search}`)}`);
}

// ------------------------------------------------------------------- 传输层

export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  /**
   * ⭐ 2026-10-09：**数组 ⇒ 重复参数**（每个元素 `append` 一次）。
   * ⛔ **不是**逗号拼接：值里的逗号是**内容**（素材名是自由文本、可能含逗号），
   * 拼成一个字符串会被拆错 ⇒ "取到"变"缺失" = **说谎**。
   */
  | readonly string[];

export interface RequestOptions {
  readonly method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export function buildUrl(path: string, query: Readonly<Record<string, QueryValue>> | undefined): string {
  const params = new URLSearchParams();
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === "") continue;
      if (Array.isArray(value)) {
        // ⭐ 重复参数：每个元素 append 一次（⛔ 不 `String(value)` 拼成 "a,b"）。
        for (const item of value) {
          if (item === "") continue;
          params.append(key, item);
        }
        continue;
      }
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length === 0 ? path : `${path}?${qs}`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.trim() === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * 发起一次请求并解析信封。
 * - 401 → 走 `handleUnauthenticated()`（清会话 + 跳登录），随后仍抛出结构化错误，
 *   让调用方自己决定要不要渲染错误态。
 * - AbortError 原样抛出，交给 hook 忽略。
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiEnvelope<T>> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  if (options.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      credentials: "same-origin",
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiClientError({
      code: "NETWORK_ERROR",
      message: error instanceof Error ? error.message : "网络请求失败",
      httpStatus: 0,
    });
  }

  if (response.status === 204) {
    return { data: undefined as T, meta: EMPTY_META };
  }

  const body = await readBody(response);

  if (!response.ok) {
    const failure = parseErrorBody(response.status, body);
    if (failure.isUnauthenticated) handleUnauthenticated();
    throw failure;
  }

  return parseEnvelope<T>(body);
}

// ------------------------------------------------------------------- 端点

/** `POST /v1/auth/login` → 200 `{ data: { user } }`。 */
export async function apiLogin(username: string, password: string): Promise<AuthUser> {
  const envelope = await request<{ user: AuthUser }>("/v1/auth/login", {
    method: "POST",
    body: { username, password },
  });
  return envelope.data.user;
}

/** `POST /v1/auth/logout` → 204。 */
/**
 * ⭐ 自助改密（**基本安全能力，不挂任何功能点**）。
 * 服务端口径：body `{ currentPassword, newPassword }`（**校验当前密码**；空/非串 ⇒ 字段级 `INVALID_ARGUMENT`）。
 * ⚠️ `must_change_password === true` 时，只有本接口与 `/auth/me` 可达（其余一律 `PASSWORD_CHANGE_REQUIRED`）。
 */
export async function apiChangeOwnPassword(input: {
  readonly currentPassword: string;
  readonly newPassword: string;
}): Promise<void> {
  await request<unknown>("/v1/auth/change-password", {
    method: "POST",
    body: { currentPassword: input.currentPassword, newPassword: input.newPassword },
  });
}

export async function apiLogout(): Promise<void> {
  await request<undefined>("/v1/auth/logout", { method: "POST" });
}

/** `GET /v1/auth/me` → 200 `{ data: { user } }`；未登录 401 `UNAUTHENTICATED`。 */
export async function apiFetchMe(signal?: AbortSignal): Promise<AuthUser> {
  const envelope = await request<{ user: AuthUser }>("/v1/auth/me", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.user;
}

/**
 * `GET /v1/permissions/users`（C1 Phase 3）→ **自己分支内**的用户（含内联 grants）。
 *
 * 服务端已按分支裁剪（管理员 A 看不到 B 创建的用户）；前端只是渲染，**不做可见范围推断**。
 */
export async function apiFetchPermissionUsers(signal?: AbortSignal): Promise<readonly PermissionUser[]> {
  const envelope = await request<{ items: readonly PermissionUser[] }>("/v1/permissions/users", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.items;
}

/** `PUT /v1/permissions/users/:userId/grants` 的请求体：**整组替换**（不是增量）。 */
export interface UpdateUserGrantsInput {
  readonly shopIds: readonly string[];
  readonly features: Readonly<Record<string, "read" | "write">>;
}

/**
 * `PUT /v1/permissions/users/:userId/grants`（C1 Phase 3）：整组替换 + 同事务审计，
 * 返回 diff 供 UI 显示「新增 / 移除」。
 *
 * ⚠️ 越权由**服务端**拒绝（契约 `validateGrantAssignment`）；前端保存前先本地跑一遍
 * 只是为了**逐条显示原因**（体验），不是安全边界。
 */
export async function apiUpdateUserGrants(
  userId: string,
  input: UpdateUserGrantsInput,
): Promise<PermissionGrantDiff> {
  const envelope = await request<PermissionGrantDiff>(
    `/v1/permissions/users/${encodeURIComponent(userId)}/grants`,
    { method: "PUT", body: input },
  );
  return envelope.data;
}

// ------------------------------------------------ 用户生命周期（新增用户 / 改角色 / 停用 / 重置口令）
//
// 口径来源 = 契约 `packages/contracts/src/user-management.ts`（`USER_ROLES` / payload 形状）。
// ⚠️ `password` **只在请求里出现**：响应无口令/哈希，前端也不记日志、不回显。

/** `POST /v1/permissions/users` 的请求体（形状 = 契约 `CreateUserPayload`）。 */
export interface CreatePermissionUserInput {
  readonly username: string;
  readonly password: string;
  /** ⭐ 角色名：**来自 `GET /v1/roles`**（可自定义）⇒ 不再是写死的三值联合。 */
  readonly role: string;
}

/** `PATCH /v1/permissions/users/:userId` 的可选字段（**至少给一个**，否则服务端 400 `no_changes`）。 */
export interface UpdatePermissionUserInput {
  /** ⭐ 角色名：**来自 `GET /v1/roles`**（可自定义）⇒ 放宽为 `string`。 */
  readonly role?: string;
  /** `false` = 停用（服务端**同事务撤销该用户全部会话** ⇒ 立刻踢下线）；`true` = 启用。 */
  readonly isActive?: boolean;
  /** 新增用户时的初始口令；**留空/不传 ⇒ 服务端生成**一次性初始口令。重置口令时也用它。 */
  readonly password?: string;
}

/** `POST /v1/permissions/users`（201）：新增用户（`createdBy`/`branchPath` 由服务端生成，客户端不可传）。 */
export async function apiCreatePermissionUser(
  input: CreatePermissionUserInput,
): Promise<{ readonly user: PermissionUser; readonly generatedPassword: string | null }> {
  const envelope = await request<{ user: PermissionUser; generatedPassword?: string | null }>(
    "/v1/permissions/users",
    {
      method: "POST",
      // ⭐ 口令**可选**：留空（`""`/未给）⇒ **由服务端生成**一次性初始口令并置 `must_change_password`。
      body: {
        username: input.username,
        ...(input.password === undefined || input.password === "" ? {} : { password: input.password }),
        role: input.role,
      },
    },
  );
  return { user: envelope.data.user, generatedPassword: envelope.data.generatedPassword ?? null };
}

/** `PATCH /v1/permissions/users/:userId`：改角色 / 停用启用 / 重置口令（**只传给了的字段**）。 */
export async function apiUpdatePermissionUser(
  userId: string,
  input: UpdatePermissionUserInput,
): Promise<PermissionUser> {
  const envelope = await request<{ user: PermissionUser }>(
    `/v1/permissions/users/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      body: {
        ...(input.role === undefined ? {} : { role: input.role }),
        ...(input.isActive === undefined ? {} : { isActive: input.isActive }),
        ...(input.password === undefined ? {} : { password: input.password }),
      },
    },
  );
  return envelope.data.user;
}

/** `GET /v1/health/sources`。 */
export async function apiFetchCredentialHealth(signal?: AbortSignal): Promise<{
  readonly data: CredentialHealthData;
  readonly warnings: readonly ResponseWarning[];
}> {
  const envelope = await request<CredentialHealthData>("/v1/health/sources", {
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/timezones`（2026-10-05 第十五批）——**时区披露**。
 *
 * 后端同时返回体检快照（`shops` / `rows`），本页只消费 `accounts`（实时连接）
 * 与 `probeDay`（体检日，必须显示出来才能复核）。**只披露、不拦截**。
 */
export async function apiFetchTimezones(signal?: AbortSignal): Promise<{
  readonly data: TimezoneReportData;
  readonly warnings: readonly ResponseWarning[];
}> {
  const envelope = await request<TimezoneReportData>("/v1/timezones", {
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

export interface EntitiesQuery {
  readonly level: AdEntityLevel;
  readonly limit?: number;
  readonly cursor?: string | null;
}

export interface EntitiesPage {
  readonly items: readonly AdEntityRow[];
  readonly nextCursor: string | null;
  readonly warnings: readonly ResponseWarning[];
}

/** `GET /v1/entities?level=&limit=&cursor=`。 */
export async function apiFetchEntities(query: EntitiesQuery, signal?: AbortSignal): Promise<EntitiesPage> {
  const envelope = await request<{ items: readonly AdEntityRow[]; nextCursor: string | null }>("/v1/entities", {
    query: {
      level: query.level,
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    items: envelope.data.items,
    nextCursor: envelope.data.nextCursor ?? envelope.meta.nextCursor,
    warnings: envelope.meta.warnings,
  };
}

export interface EntityMetricsQueryInput {
  readonly level: AdEntityLevel;
  readonly from: string;
  readonly to: string;
  readonly shopId?: string;
  readonly currency?: string;
  /** 语言筛选（R24）；缺省 = 不筛。 */
  readonly language?: string;
  readonly limit?: number;
  readonly cursor?: string | null;
}

export interface EntityMetricsPage {
  readonly items: readonly EntityMetricsRow[];
  readonly nextCursor: string | null;
  readonly coverage: UtmCoverageSummary;
  readonly warnings: readonly ResponseWarning[];
}

/** `GET /v1/metrics/entities?level=&from=&to=&shopId=&currency=&limit=&cursor=`。 */
export async function apiFetchEntityMetrics(
  query: EntityMetricsQueryInput,
  signal?: AbortSignal,
): Promise<EntityMetricsPage> {
  const envelope = await request<EntityMetricsData>("/v1/metrics/entities", {
    query: {
      level: query.level,
      from: query.from,
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.currency === undefined ? {} : { currency: query.currency }),
      ...(query.language === undefined ? {} : { language: query.language }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.cursor === undefined || query.cursor === null ? {} : { cursor: query.cursor }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    items: envelope.data.items,
    nextCursor: envelope.data.nextCursor ?? envelope.meta.nextCursor,
    // 覆盖率缺失（旧服务）时给 0/0 而不是编造：契约层会把 0 分母变成 null。
    coverage: envelope.data.coverage ?? { shopOrderCount: 0, utmMatchedOrderCount: 0, utmCoverageRate: null },
    warnings: envelope.meta.warnings,
  };
}

// ------------------------------------------------------- 广告管理·折叠树（R20/R22/R33）

/**
 * `GET /v1/ad-tree` 的查询参数（2026-10-04 分页/懒加载接口）。
 *
 * 两种调用形态（由 `parentEntityId` 决定）：
 *  · 不带 `parentEntityId` → **系列层一页**（`limit` 默认 50、上限 200），返回 `nextCursor`；
 *  · 带 `parentEntityId` + `parentAccountId` → 该父节点的**直接子级**一层，不分页。
 */
export interface AdTreeQueryInput {
  readonly from: string;
  readonly to: string;
  /** 站点筛选（走归属物化表）。全局栏选中「全部店铺」时不传。 */
  readonly shopId?: string;
  /** 品牌筛选（归属表 JOIN shops）。全局栏选中「全部品牌」时不传。 */
  readonly brandId?: string;
  /**
   * 是否**只显示生效中的项**（三层；默认 `true`）。
   * `effectiveStatus` 为空（未同步 = 未知）时不显示；`false` = 全量（含暂停）。
   */
  readonly activeOnly?: boolean;
  /**
   * 是否**只显示选定区间内确实投放过**的实体（三层；2026-10-06 task-9 起前端默认 `false`）。
   * 判据 = 该实体在区间内、该层级、`TOTAL` + `meta_attributed` 事实里 `spend_cents > 0`。
   * 默认只按「投放中就显示」，打开「只显示有花费的」才传 `true`。
   */
  readonly deliveringOnly?: boolean;
  /**
   * **「暂停-建议看」筛选（2026-10-06 · task-3）**：`true` = 只显示「子树里存在 ≥1 条
   * 「值得看且当前暂停」广告」的**系列**。只作用于顶层系列集合；子级请求传了也会被忽略
   * （展开系列后必须仍能看到系列内其他广告）。
   */
  readonly pausedWorthWatchingOnly?: boolean;
  /** 是否请服务端附带 N（筛选文案 `暂停-建议看(N)`）；缺省不传 = 不附带。 */
  readonly includePausedWorthWatchingCount?: boolean;
  /**
   * **是否包含「排期已结束」的历史系列**（2026-10-06 · task-14）。
   *
   * 缺省不传 = 服务端默认 `false`（隐藏历史噪音；AU 站 442 条里 431 条是排期已结束）。
   * 只作用于**顶层系列集合**；子级请求（展开）不受影响。
   */
  readonly includeEnded?: boolean;
  /** 语言筛选（R24，值形如 `en` / `de` / `es`）。 */
  readonly language?: string;
  /**
   * **货号筛选（2026-10-04 第八批）**：只显示「下属 ACTIVE 广告名合集里包含任一该货号」
   * 的系列（与货号列同源解析）。逗号拼接成 `styleCodes=A,B` 下发；空数组 = 不传。
   */
  readonly styleCodes?: readonly string[];
  /**
   * **货号分组筛选（2026-10-05 第十八批）**：只显示「下属 ACTIVE 广告名解析出的货号
   * 命中任一选中组里的货号」的实体。逗号拼接成 `styleCodeGroups=g1,g2` 下发；
   * 空数组 = 不传。与 `styleCodes` 同时给时后端取**交集**（同时满足）。
   */
  readonly styleCodeGroups?: readonly string[];
  /**
   * **素材筛选（2026-10-04 第十二批 · 用户第 10 条）**：只显示「存在某条 ACTIVE 广告的
   * 名字包含任一关键词」的实体（大小写不敏感、纯子串、不做分词）。
   * 逗号拼接成 `material=a,b` 下发（沿用第八批占位字段名 `material`）；空数组 = 不传。
   */
  readonly materials?: readonly string[];
  /** 三分组基准，默认日预算。 */
  readonly scaleBasis?: AdTreeScaleBasis;
  /**
   * **列表排序基准**（2026-10-04 第四批）：默认 `spend`（按花费/最近 7 天消耗降序）。
   * 与 `scaleBasis`（分组）解耦——分组仍默认按日预算（R22）。
   */
  readonly sort?: AdTreeScaleBasis;
  /**
   * **列表排序方向**（2026-10-04 第七批）：`desc`（默认）或 `asc`。
   * 只作用于排序键；分组（Pareto 分档）不受影响。
   */
  readonly sortDirection?: AdTreeSortDirection;
  readonly accountId?: string;
  /** 系列层页大小（后端默认 50、上限 200）。 */
  readonly limit?: number;
  /** 系列层下一页游标（来自上一次响应的 `nextCursor`）。 */
  readonly cursor?: string;
  /** 子级模式：父节点所在账户。 */
  readonly parentAccountId?: string;
  /** 子级模式：父节点 id。 */
  readonly parentEntityId?: string;
  /** 父节点层级（给了更准；缺省由后端查台账推断）。 */
  readonly parentLevel?: "campaign" | "adset";
  /**
   * ⭐ 2026-10-30（国家筛选）：ISO-2 国家码（`[]` = 全部国家）。后端按 `country=XX` 切片事实；
   * 空数组**不传**参数 ⇒ 后端锁 `breakdown_key='TOTAL'`（与改造前逐字一致）。
   */
  readonly countries?: readonly string[];
}

/** `GET /v1/ad-tree` 的返回值：`data` 拆平 + `meta.warnings` 一起带出。 */
export interface AdTreePage {
  readonly nodes: readonly AdTreeNode[];
  readonly currencySummaries: readonly AdTreeCurrencySummaryRow[];
  /** 按币种的汇总指标（2026-10-12 · 用户第 ① 条，可选；老后端缺省 ⇒ 保持空列）。 */
  readonly currencyMetrics?: readonly AdTreeCurrencyMetrics[];
  readonly coverage: AdTreeCoverage;
  readonly totals: AdTreeTotals;
  readonly nextCursor: string | null;
  readonly warnings: readonly ResponseWarning[];
  /** `暂停-建议看(N)` 的 N（task-3）；未请求 / 旧服务时为 `undefined`。 */
  readonly pausedWorthWatching?: { readonly adCount: number; readonly campaignCount: number };
  /**
   * 不投放原因计数（task-14）；旧服务 / 子级模式为 `undefined`（页面不编造 0）。
   */
  readonly notDeliveringSummary?: Readonly<Record<NotDeliveringReasonKind, number>>;
  /**
   * ⭐ 2026-10-30（国家筛选）：**实际生效**的国家（后端回显；`[]` = 未筛选 = 全部国家）。
   * 旧服务缺该字段 ⇒ `undefined`（页面按"未筛选"处理，⛔ 不自己推断）。
   */
  readonly appliedCountries?: readonly string[];
}

/**
 * `GET /v1/ad-tree?from=&to=&shopId=&brandId=&activeOnly=&language=&scaleBasis=&limit=&cursor=&parentAccountId=&parentEntityId=`。
 *
 * 不做任何默认值填充：`activeOnly` 由页面显式传（默认 true 是用户口径），
 * `coverage` / `totals` 缺失时给**明确的空值**（页面会如实显示「覆盖未知」而不是假装全量）。
 */
export async function apiFetchAdTree(query: AdTreeQueryInput, signal?: AbortSignal): Promise<AdTreePage> {
  const envelope = await request<AdTreeData>("/v1/ad-tree", {
    query: {
      from: query.from,
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.brandId === undefined ? {} : { brandId: query.brandId }),
      ...(query.activeOnly === undefined ? {} : { activeOnly: query.activeOnly }),
      ...(query.deliveringOnly === undefined ? {} : { deliveringOnly: query.deliveringOnly }),
      ...(query.pausedWorthWatchingOnly === undefined
        ? {}
        : { pausedWorthWatchingOnly: query.pausedWorthWatchingOnly }),
      ...(query.includePausedWorthWatchingCount === undefined
        ? {}
        : { includePausedWorthWatchingCount: query.includePausedWorthWatchingCount }),
      // task-14：「包含已结束」（缺省不传 = 服务端默认 false = 隐藏历史噪音）。
      ...(query.includeEnded === undefined ? {} : { includeEnded: query.includeEnded }),
      ...(query.language === undefined ? {} : { language: query.language }),
      // 货号筛选：逗号拼接（空数组不传 → 后端 `undefined` = 不筛）。
      ...(query.styleCodes === undefined || query.styleCodes.length === 0
        ? {}
        : { styleCodes: query.styleCodes.join(",") }),
      // 货号分组筛选（第十八批）：逗号拼接组 id（空数组不传 → 后端 `undefined` = 不筛）。
      ...(query.styleCodeGroups === undefined || query.styleCodeGroups.length === 0
        ? {}
        : { styleCodeGroups: query.styleCodeGroups.join(",") }),
      // 素材筛选（第十二批）：同样逗号拼接；后端按**纯子串**匹配广告名（大小写不敏感）。
      ...(query.materials === undefined || query.materials.length === 0
        ? {}
        : { material: query.materials.join(",") }),
      // ⭐ 国家筛选（2026-11-02）：逗号拼接 `countries=PH,SG`；空数组不传 ⇒ 后端锁 TOTAL（零回归）。
      ...(query.countries === undefined || query.countries.length === 0
        ? {}
        : { countries: query.countries.join(",") }),
      ...(query.scaleBasis === undefined ? {} : { scaleBasis: query.scaleBasis }),
      ...(query.sort === undefined ? {} : { sort: query.sort }),
      ...(query.sortDirection === undefined ? {} : { sortDirection: query.sortDirection }),
      ...(query.accountId === undefined ? {} : { accountId: query.accountId }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.parentAccountId === undefined ? {} : { parentAccountId: query.parentAccountId }),
      ...(query.parentEntityId === undefined ? {} : { parentEntityId: query.parentEntityId }),
      ...(query.parentLevel === undefined ? {} : { parentLevel: query.parentLevel }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    nodes: envelope.data.nodes,
    currencySummaries: envelope.data.currencySummaries,
    // 2026-10-12（用户第 ① 条）：按币种汇总指标；老后端没有该键 ⇒ 不带（页面保持空列 ✓）。
    ...(envelope.data.currencyMetrics === undefined
      ? {}
      : { currencyMetrics: envelope.data.currencyMetrics }),
    // 旧服务/空响应时不编造：全 0 的覆盖行在视觉上等价于「没有覆盖率信息」。
    coverage: envelope.data.coverage ?? {
      accountsIncluded: 0,
      entitiesTotal: 0,
      orphanCount: 0,
      missingFactsEntities: 0,
    },
    // 同上：缺失时给"无法合计"的形状，而不是 0（0 会被读成"这个页面没有规模"）。
    totals: envelope.data.totals ?? {
      campaignCount: null,
      entityCount: envelope.data.nodes.length,
      scaleTotalCents: null,
      currencyCount: 0,
    },
    nextCursor: envelope.data.nextCursor ?? envelope.meta.nextCursor ?? null,
    warnings: envelope.meta.warnings,
    // task-3：N 只在服务端给出时透传（未请求 / 旧服务 → 不出现，页面不编造 0）。
    ...(envelope.data.pausedWorthWatching === undefined
      ? {}
      : { pausedWorthWatching: envelope.data.pausedWorthWatching }),
    // task-14：四类不投放原因计数（旧服务 / 子级模式 → 不出现，页面不编造 0）。
    ...(envelope.data.notDeliveringSummary === undefined
      ? {}
      : { notDeliveringSummary: envelope.data.notDeliveringSummary }),
  };
}

/** `GET /v1/ad-tree/history` 的查询参数（历史弹窗）。 */
export interface AdTreeHistoryQueryInput {
  readonly accountId: string;
  readonly entityId: string;
  readonly level: AdEntityLevel;
  /** 窗口上界（业务日，含）；页面传当前展示区间的上界，弹窗与列表同一条时间线。 */
  readonly to: string;
  readonly shopId?: string;
  readonly language?: string;
}

/**
 * `GET /v1/ad-tree/history?accountId=&entityId=&level=&to=&shopId=&language=`。
 *
 * 返回**两块**连续序列（12 个自然月 + 30 天），口径与 `/v1/ad-tree` 逐字同源；
 * `warnings` 随 `meta.warnings` 带出。`envelope.data` 缺失时给**明确的空形状**
 * （页面据此显示「无数据」而不是伪造 0）。
 */
/**
 * ⭐ 2026-10-30（国家筛选）：**该店铺实际投放过的国家**（筛选器选项的**唯一来源**）。
 *
 * ⛔ 前端不许从系列名/国家组猜选项；后端返回 ISO-2 大写升序。
 */
export async function apiFetchAdTreeCountries(
  query: { readonly shopId?: string },
  signal?: AbortSignal,
): Promise<readonly string[]> {
  const envelope = await request<{ readonly countries: readonly string[] }>("/v1/ad-tree/countries", {
    query: { ...(query.shopId === undefined ? {} : { shopId: query.shopId }) },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.countries;
}

export async function apiFetchAdTreeHistory(
  query: AdTreeHistoryQueryInput,
  signal?: AbortSignal,
): Promise<{ readonly data: AdTreeHistoryData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<AdTreeHistoryData>("/v1/ad-tree/history", {
    query: {
      accountId: query.accountId,
      entityId: query.entityId,
      level: query.level,
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.language === undefined ? {} : { language: query.language }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/shop-history?to=&shopId=`（2026-10-04 第九批 · 「概览」按钮）。
 *
 * 行 = 最近 7 天 + 过去 6 个自然月；列全是 USD（三条来源折算），同比在服务端算好。
 * `shopId` 缺省 = 全部店铺的折算全局口径（不是"某个店"）。
 */
export async function apiFetchShopHistory(
  query: { readonly to: string; readonly shopId?: string },
  signal?: AbortSignal,
): Promise<{ readonly data: ShopHistoryData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<ShopHistoryData>("/v1/shop-history", {
    query: {
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/shop-metrics?to=[&from=]`（2026-10-06 · 经营概览「按店铺」表，task-6 第 3 条；
 * `from` 为 task-26 纯加法）。
 *
 * **一行 = 一家店铺**，取该店的整店口径（USD 折算分）：
 * 销售额 / 花费合计 / Meta 花费 / Google 花费 / Meta ROAS / GG ROAS / Shopify ROAS。
 * · 传 `from` → 窗口 = `[from, to]`（与上方 KPI / 趋势图**同一个窗口**）；
 * · 不传 `from` → 既有行为（`[to-7, to-1]`，逐字不变）。
 * 口径唯一真相源是契约 `shop-history.ts`（服务端算好，前端只渲染；`null` → 「—」）。
 */
export async function apiFetchShopMetrics(
  query: { readonly to: string; readonly from?: string },
  signal?: AbortSignal,
): Promise<{ readonly data: ShopMetricsData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<ShopMetricsData>("/v1/shop-metrics", {
    query: {
      to: query.to,
      ...(query.from === undefined || query.from === "" ? {} : { from: query.from }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/google-metrics?from=&to=[&shopId=][&convertUsd=true | &currency=]`
 * （2026-10-06 · task-6 第 1(c) 条 `Google ROAS`）。
 *
 * 口径在契约 `google-metrics.ts`（同币种求和 + `attributedRoas`；分母 ≤ 0 / 无行 → null）。
 * **币种必须二选一**：全部店铺 → `convertUsd: true`；单店 → `currency`（该店币种）。
 * 两者都不给 → 后端 400（本端点不做跨币种裸加）。
 */
export async function apiFetchGoogleMetrics(
  query: {
    readonly from: string;
    readonly to: string;
    readonly shopId?: string;
    readonly convertUsd?: boolean;
    readonly currency?: string;
  },
  signal?: AbortSignal,
): Promise<{ readonly data: GoogleMetricsData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<GoogleMetricsData>("/v1/google-metrics", {
    query: {
      from: query.from,
      to: query.to,
      ...(query.shopId === undefined || query.shopId === "" ? {} : { shopId: query.shopId }),
      ...(query.convertUsd === true ? { convertUsd: true } : {}),
      ...(query.currency === undefined || query.currency === "" ? {} : { currency: query.currency }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/spend-overview`（2026-10-05 第十九批 · 用户第 2 条）。
 *
 * 行 = 按月（最近 6 个自然月）或按天（最近 30 天）+ 底部汇总栏；
 * 金额全是 USD 折算分，占比单位 **ppm**，分母 0 / 缺失 → `null`（页面渲染「—」）。
 *
 * `scope=brand`（缺省）时 `shopId` **不参与过滤**（数据是品牌合计，只回显）；
 * 只有 `scope=shop` 才把 `shopId` 传下去（页面上的「只看该店铺」显式开关）。
 */
export async function apiFetchSpendOverview(
  query: {
    readonly granularity: "month" | "day";
    readonly to?: string;
    readonly months?: number;
    readonly days?: number;
    readonly scope?: "brand" | "shop";
    readonly shopId?: string;
    readonly brandId?: string;
  },
  signal?: AbortSignal,
): Promise<{ readonly data: SpendOverviewData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<SpendOverviewData>("/v1/spend-overview", {
    query: {
      granularity: query.granularity,
      ...(query.to === undefined || query.to === "" ? {} : { to: query.to }),
      ...(query.months === undefined ? {} : { months: query.months }),
      ...(query.days === undefined ? {} : { days: query.days }),
      ...(query.scope === undefined ? {} : { scope: query.scope }),
      ...(query.shopId === undefined || query.shopId === "" ? {} : { shopId: query.shopId }),
      ...(query.brandId === undefined || query.brandId === "" ? {} : { brandId: query.brandId }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/sync/status[?shopId]`。`platforms` 是新的一层（平台优先），`shops` 保留。 */
export async function apiFetchSyncStatus(
  query: { readonly shopId?: string } = {},
  signal?: AbortSignal,
): Promise<{
  readonly shops: readonly ShopSyncStatusRow[];
  readonly platforms: readonly SyncPlatformStatus[];
  readonly warnings: readonly ResponseWarning[];
}> {
  const envelope = await request<{
    shops: readonly ShopSyncStatusRow[];
    platforms: readonly SyncPlatformStatus[];
  }>("/v1/sync/status", {
    query: { ...(query.shopId === undefined ? {} : { shopId: query.shopId }) },
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    shops: envelope.data.shops,
    // 防御式兜底：旧后端不带 `platforms` 时给空数组（页面显式渲染空态，不崩）。
    platforms: envelope.data.platforms ?? [],
    warnings: envelope.meta.warnings,
  };
}

/**
 * `GET /v1/data-readiness`（2026-10-05 第十七批）——**轻量**就绪度端点。
 *
 * 只回三个平台的真实数据日 / 预期产出日 + 两个视图（跨平台 / 纯 Meta）在不带区间时的
 * 就绪度。区间相关的 `pendingDates` / `settled` 由页面用契约的同一个纯函数
 * `dataReadiness` 现算（见 `hooks/useDataReadiness.ts`），因此端点不会被区间组合撑大，
 * 判定也仍然只有一份实现。
 */
export async function apiFetchDataReadiness(signal?: AbortSignal): Promise<DataReadinessData> {
  const envelope = await request<DataReadinessData>("/v1/data-readiness", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

export interface MetricsQuery {
  readonly level: AdEntityLevel | "account";
  readonly scope: string;
  readonly currency: string;
  readonly from: string;
  readonly to: string;
  readonly shopId?: string;
  /**
   * `true` = 跨币种 USD 折算合计（概览页「全部店铺」时使用）。
   * 缺省 / `false` 时**不写这个参数**，请求与改动前逐字一致（单店语义不变）。
   */
  readonly convertUsd?: boolean;
}

/** `GET /v1/metrics/summary`。 */
export async function apiFetchMetricsSummary(
  query: MetricsQuery,
  signal?: AbortSignal,
): Promise<{ readonly data: MetricsSummaryData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<MetricsSummaryData>("/v1/metrics/summary", {
    query: {
      level: query.level,
      scope: query.scope,
      currency: query.currency,
      from: query.from,
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.convertUsd === true ? { convertUsd: true } : {}),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/metrics/series`（按业务日）。 */
export async function apiFetchMetricsSeries(
  query: MetricsQuery,
  signal?: AbortSignal,
): Promise<readonly MetricSeriesPoint[]> {
  const envelope = await request<readonly MetricSeriesPoint[]>("/v1/metrics/series", {
    query: {
      level: query.level,
      scope: query.scope,
      currency: query.currency,
      from: query.from,
      to: query.to,
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.convertUsd === true ? { convertUsd: true } : {}),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/creatives`（素材看板 C2 · task-23）。
 *
 * 缺省参数（`tab` 不传）回到「在投广告 + 按素材名聚合」，与后端缺省逐字一致。
 * 页面必须显式展示 `warnings`（截断 / 缺汇率 / 时区回落），**不得静默**。
 */
export async function apiFetchCreatives(
  query: {
    readonly shopId: string;
    /** `active` 在投 / `paused` 暂停 / `all` 全部（值域与后端白名单一致）。 */
    readonly tab?: "active" | "paused" | "all";
    readonly duplicatesOnly?: boolean;
    readonly sort?: string;
    readonly sortDirection?: "asc" | "desc";
    readonly limit?: number;
    readonly cursor?: string;
    /** 货号筛选（逗号分割多个；OR、精确、大小写不敏感）—— 服务端下推、全量匹配。 */
    readonly styleCodes?: string;
    /** 素材名筛选（包含匹配）—— 服务端下推、全量匹配。 */
    readonly materialName?: string;
    /**
     * ⭐ 2026-10-09（用户："很多素材没有 素材ROAS(修正)，应该只要有花费就有"）：
     * **批量精确**匹配**规范素材名**（服务端 `btrim(规范名) = ANY(名字)`，**大小写敏感**）。
     * ⚠️ 与单数 `materialName`（**子串 ILIKE**）语义不同；上限 500，>500 ⇒ 400。
     * ⚠️ **重复参数**（每名字一次 append）—— `buildUrl` 对数组走 `append`，⛔ 不逗号拼串。
     */
    readonly materialNames?: readonly string[];
  },
  signal?: AbortSignal,
): Promise<CreativeBoardData & { readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<CreativeBoardData>("/v1/creatives", {
    query: {
      shopId: query.shopId,
      ...(query.tab === undefined ? {} : { tab: query.tab }),
      ...(query.duplicatesOnly === true ? { duplicatesOnly: 1 } : {}),
      ...(query.sort === undefined ? {} : { sort: query.sort }),
      ...(query.sortDirection === undefined ? {} : { sortDirection: query.sortDirection }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.styleCodes === undefined || query.styleCodes === "" ? {} : { styleCodes: query.styleCodes }),
      ...(query.materialName === undefined || query.materialName === ""
        ? {}
        : { materialName: query.materialName }),
      ...(query.materialNames === undefined || query.materialNames.length === 0
        ? {}
        : { materialNames: query.materialNames }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { ...envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/creatives/ads`（创意 ID 弹窗；与修正 ROAS 抽屉是两个入口）。 */
export async function apiFetchCreativeAds(
  query: { readonly shopId: string; readonly canonicalName: string },
  signal?: AbortSignal,
): Promise<{
  readonly canonicalName: string;
  readonly items: readonly CreativeMaterialAd[];
  readonly warnings: readonly ResponseWarning[];
  /**
   * ⭐ 2026-10-08：窗口披露（后端给）。**不要自己算日期** —— 直接渲染这里的字段。
   * 缺省（老后端/未提供）⇒ `undefined`，UI 不渲染窗口句（不是"窗口是空的"）。
   */
  readonly window?: {
    readonly today: string;
    readonly settledDate: string;
    readonly from7: string;
    readonly to7: string;
    readonly excludesToday: boolean;
    readonly note: string;
  };
}> {
  const envelope = await request<{
    canonicalName: string;
    items: readonly CreativeMaterialAd[];
  }>("/v1/creatives/ads", {
    query: { shopId: query.shopId, canonicalName: query.canonicalName },
    ...(signal === undefined ? {} : { signal }),
  });
  return { ...envelope.data, warnings: envelope.meta.warnings };
}/**
 * `GET /v1/creatives/corrected`（task-24 抽屉，懒加载）。
 *
 * 返回某素材的**自然月**历史（按账户分节）：`月份 | 花费 | 真实ROAS | 当月账户ROAS | 折算ROAS | 可用天数`
 * + 累计行。跨账户素材 `crossAccount=true` 且分账户，**绝不合并**。
 */
export async function apiFetchCreativeCorrected(
  query: {
    readonly shopId: string;
    readonly materialName: string;
    readonly accountId?: string;
  },
  signal?: AbortSignal,
): Promise<CreativeCorrectedDrilldownData & { readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<CreativeCorrectedDrilldownData>("/v1/creatives/corrected", {
    query: {
      shopId: query.shopId,
      materialName: query.materialName,
      ...(query.accountId === undefined ? {} : { accountId: query.accountId }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { ...envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/style-codes`。 */
export async function apiFetchStyleCodes(
  query: {
    readonly shopId: string;
    readonly from: string;
    readonly to: string;
    readonly limit?: number;
    readonly sort?: string;
    readonly order?: "asc" | "desc";
    /**
     * `true` = 金额换 USD 折算列（用户口径：货号看板默认 USD）。
     * 缺省 / `false` = 单店原币，请求与改动前逐字一致。
     */
    readonly convertUsd?: boolean;
    /**
     * R24 语言筛选；缺省 = 全部（**不写这个参数**，请求与改动前逐字一致）。
     * `'unknown'` 合法——专门看"没归到系列 / 系列没绑语言"的那部分。
     */
    readonly language?: StyleCodeBoardLanguage;
  },
  signal?: AbortSignal,
): Promise<{
  readonly items: readonly StyleCodeBoardRow[];
  readonly truncated: boolean;
  /**
   * 当前窗口口径的语言分布（**不受 language 筛选影响**）：页面必须用它显示覆盖率，
   * 不得把 unknown 静默藏在总数里。
   */
  readonly languageBreakdown: readonly StyleCodeBoardLanguageRow[];
  readonly warnings: readonly ResponseWarning[];
}> {
  const envelope = await request<{
    items: readonly StyleCodeBoardRow[];
    truncated: boolean;
    languageBreakdown: readonly StyleCodeBoardLanguageRow[];
  }>("/v1/style-codes", {
    query: {
      shopId: query.shopId,
      from: query.from,
      to: query.to,
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.sort === undefined ? {} : { sort: query.sort }),
      ...(query.order === undefined ? {} : { order: query.order }),
      ...(query.convertUsd === true ? { convertUsd: true } : {}),
      ...(query.language === undefined ? {} : { language: query.language }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    items: envelope.data.items,
    truncated: envelope.data.truncated,
    languageBreakdown: envelope.data.languageBreakdown,
    // 截断提示与缺汇率告警都在 meta.warnings：页面必须显式展示，不得静默。
    warnings: envelope.meta.warnings,
  };
}

export interface ActionRequestPayload {
  readonly action: MetaActionName;
  readonly targets: readonly ActionTargetInput[];
  readonly budgetMinorUnits: number | null;
  /** 仅 `rename` 使用（新名称）；其它动作必须省略（服务端严格匹配）。 */
  readonly name?: string | null;
  /**
   * 预算**显式选层**（P2 任务 4）：只在 `budgetScope === "both"` 的预算动作上**必须**给，
   * 且必须等于目标实体层（服务端校验，不匹配直接拒绝）。
   */
  readonly budgetLevel?: "campaign" | "adset" | null;
  readonly dryRun: boolean;
  readonly confirm: boolean;
  readonly note?: string;
}

/**
 * `POST /v1/actions`（写路径）。
 *
 * ⚠️ 服务端三层闸门：`dryRun:true` 只预演；真写必须 `dryRun:false` **且** `confirm:true`
 * **且** 服务端 `AMS_ALLOW_META_WRITES=1`。客户端**不能**也**不应该**绕过其中任何一层。
 */
export async function apiPostActions(payload: ActionRequestPayload, signal?: AbortSignal): Promise<ActionPlanResult> {
  const envelope = await request<ActionPlanResult>("/v1/actions", {
    method: "POST",
    body: {
      action: payload.action,
      targets: payload.targets,
      budgetMinorUnits: payload.budgetMinorUnits,
      // 改名动作必带 `name`；其余动作**不能**带（服务端严格匹配，带了会 400 INVALID_NAME）。
      ...(payload.name === undefined || payload.name === null ? {} : { name: payload.name }),
      // 预算显式选层（P2 任务 4）：只有 `both` 的预算动作需要；非 null 才带。
      ...(payload.budgetLevel === undefined || payload.budgetLevel === null ? {} : { budgetLevel: payload.budgetLevel }),
      dryRun: payload.dryRun,
      confirm: payload.confirm,
      ...(payload.note === undefined ? {} : { note: payload.note }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/ad-edit/:level/:entityId`（2026-10-05 第二十二批）：广告编辑面板的**只读**数据。
 *
 * 服务端会在这里向 Meta **现查**当前值（只读 GET）+ 查台账使用率 + 跑契约层判定；
 * 前端不重复任何判定，只渲染。与 `warnings` 一起返回，便于把「实时读不到」如实展示。
 */
export async function apiGetAdEdit(
  input: { readonly level: AdEntityLevel; readonly entityId: string; readonly accountId: string },
  signal?: AbortSignal,
): Promise<{ readonly panel: AdEditPanel; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<AdEditPanel>(
    `/v1/ad-edit/${encodeURIComponent(input.level)}/${encodeURIComponent(input.entityId)}`,
    {
      query: { accountId: input.accountId },
      ...(signal === undefined ? {} : { signal }),
    },
  );
  return { panel: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `POST /v1/merges/preview`（**只读规划**：不写库、不发 Meta 写请求）。
 *
 * 用户口径：并入前要有预览页显示前后变化。混勾 / 跨账户 / 空 / 源对象查不到
 * 由服务端返回 400（`details[].issue` 带机器可读码）。
 *
 * 2026-10-05：返回值里带上 `meta.warnings` **原文**——`MERGE_DESTINATION_DERIVED`
 * （未选目标 → 系统按源系列推导）必须让页面看得见，不能只留在 data 里被丢掉。
 */
export async function apiPostMergePreview(
  payload: Omit<MergeRequestBody, "confirm">,
  signal?: AbortSignal,
): Promise<{ readonly data: MergePreviewData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<MergePreviewData>("/v1/merges/preview", {
    method: "POST",
    body: payload,
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `GET /v1/merges/destinations`（**只读**目标选择候选：同账户的系列 + 按系列分组的广告组）。
 *
 * 真实账户实测 **804 个系列 / 858 个广告组**，因此：
 *  · 必须支持 `search`（服务端 `ILIKE`）与 `limit`（服务端截断，`MERGE_DESTINATION_TRUNCATED`）；
 *  · 端口未接入时后端返回 **503 `MERGE_DESTINATIONS_UNAVAILABLE`**，
 *    这里**不吞**成「加载失败」——调用方按 `ApiClientError.code` 显示后端原文。
 */
export async function apiFetchMergeDestinations(
  query: { readonly accountId: string; readonly search?: string; readonly limit?: number },
  signal?: AbortSignal,
): Promise<{ readonly data: MergeDestinationsData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<MergeDestinationsData>("/v1/merges/destinations", {
    query: {
      accountId: query.accountId,
      ...(query.search === undefined ? {} : { search: query.search }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/**
 * `POST /v1/merges/execute`（**真写 Meta**）。
 *
 * 服务端三重闸门：`confirm:true` + `AMS_ALLOW_META_WRITES=1` + 三个写端口齐备，
 * 且执行前会**重新规划**（TOCTOU 防线）。客户端不绕过任何一层。
 */
export async function apiPostMergeExecute(
  payload: Omit<MergeRequestBody, "confirm">,
  signal?: AbortSignal,
): Promise<MergeExecuteResult> {
  const envelope = await request<MergeExecuteResult>("/v1/merges/execute", {
    method: "POST",
    body: { ...payload, confirm: true },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** `GET /v1/merges/links`（源 id → 新 id 的映射；「系统记住之前的广告 ID」）。 */
export async function apiFetchMergeLinks(
  query: {
    readonly mergeId?: string;
    readonly targetEntityId?: string;
    readonly sourceEntityId?: string;
    readonly limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<readonly MergeLinkRecordRow[]> {
  const envelope = await request<{ items: readonly MergeLinkRecordRow[] }>("/v1/merges/links", {
    query: {
      ...(query.mergeId === undefined ? {} : { mergeId: query.mergeId }),
      ...(query.targetEntityId === undefined ? {} : { targetEntityId: query.targetEntityId }),
      ...(query.sourceEntityId === undefined ? {} : { sourceEntityId: query.sourceEntityId }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.items;
}

/** `GET /v1/actions/log`。 */
export async function apiFetchActionLog(
  query: {
    readonly module?: string;
    readonly action?: string;
    readonly result?: string;
    readonly from?: string;
    readonly to?: string;
    readonly limit?: number;
  } = {},
  signal?: AbortSignal,
): Promise<readonly OperationLogEntryRow[]> {
  const envelope = await request<{ items: readonly OperationLogEntryRow[] }>("/v1/actions/log", {
    query: {
      ...(query.module === undefined ? {} : { module: query.module }),
      ...(query.action === undefined ? {} : { action: query.action }),
      ...(query.result === undefined ? {} : { result: query.result }),
      ...(query.from === undefined ? {} : { from: query.from }),
      ...(query.to === undefined ? {} : { to: query.to }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.items;
}

/**
 * `GET /v1/actions/budget-history`（第十二批 · 用户第 8 条）。
 *
 * 返回**去重后**（按账户时区每天只留最后一条）的最近 N 次**真实**预算调整；
 * `beforeBudgetMinorUnits === null` 表示这条历史没有前值（页面如实显示，不编）。
 *
 * `includeMeta`（2026-10-05 第十六批 · 用户第 4 条）：置 true 时后端**按需**拉一次
 * Meta 账户审计日志并合并（只读、有缓存、有翻页上限），响应里的 `metaAudit` 如实
 * 说明 Meta 侧是否可用 / 是否被截断。
 */
export async function apiFetchBudgetHistory(
  query: {
    readonly accountId: string;
    readonly level: AdEntityLevel;
    readonly entityId: string;
    /** 可选：按父实体汇总（ABO 批量弹窗 = 该系列下全部广告组的调整记录）。 */
    readonly parentEntityId?: string;
    readonly limit?: number;
    /** 合并 Meta 审计日志（所有渠道的改动）。 */
    readonly includeMeta?: boolean;
  },
  signal?: AbortSignal,
): Promise<BudgetHistoryData> {
  const envelope = await request<BudgetHistoryData>("/v1/actions/budget-history", {
    query: {
      accountId: query.accountId,
      level: query.level,
      entityId: query.entityId,
      ...(query.parentEntityId === undefined ? {} : { parentEntityId: query.parentEntityId }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.includeMeta === true ? { include: "meta" } : {}),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** 健康检查（无信封）——用于「API 是否可达」的显式探测。 */
export async function apiHealthz(signal?: AbortSignal): Promise<unknown> {
  const response = await fetch("/healthz", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
    ...(signal === undefined ? {} : { signal }),
  });
  return readBody(response);
}

// --------------------------------------------------------- 品牌 / 店铺命名

/** `GET /v1/brands`（迁移 0013）：品牌 + 名下站点数。 */
export async function apiFetchBrands(
  signal?: AbortSignal,
): Promise<{ readonly items: readonly BrandAdminRow[]; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<{ items: readonly BrandAdminRow[] }>("/v1/brands", {
    ...(signal === undefined ? {} : { signal }),
  });
  return { items: envelope.data.items, warnings: envelope.meta.warnings };
}

export interface CreateBrandInput {
  readonly name: string;
  /** 缺省 0（后端约定）。 */
  readonly sortOrder?: number;
}

/** `POST /v1/brands`（仅 admin）。 */
export async function apiCreateBrand(input: CreateBrandInput, signal?: AbortSignal): Promise<BrandAdminRow> {
  const envelope = await request<{ brand: BrandAdminRow }>("/v1/brands", {
    method: "POST",
    body: {
      name: input.name,
      ...(input.sortOrder === undefined ? {} : { sortOrder: input.sortOrder }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.brand;
}

export interface UpdateBrandPatch {
  readonly name?: string;
  readonly sortOrder?: number;
  readonly status?: BrandStatus;
}

/** `PATCH /v1/brands/:brandId`（仅 admin）：改名 / 排序 / 归档。 */
export async function apiUpdateBrand(
  brandId: string,
  patch: UpdateBrandPatch,
  signal?: AbortSignal,
): Promise<BrandAdminRow> {
  const envelope = await request<{ brand: BrandAdminRow }>(`/v1/brands/${encodeURIComponent(brandId)}`, {
    method: "PATCH",
    body: {
      ...(patch.name === undefined ? {} : { name: patch.name }),
      ...(patch.sortOrder === undefined ? {} : { sortOrder: patch.sortOrder }),
      ...(patch.status === undefined ? {} : { status: patch.status }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.brand;
}

/** `GET /v1/shops`：站点清单（含未命名/未归属的行，绝不因此隐藏站点）。 */
export async function apiFetchShops(
  signal?: AbortSignal,
): Promise<{ readonly items: readonly ShopAdminRow[]; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<{ items: readonly ShopAdminRow[] }>("/v1/shops", {
    ...(signal === undefined ? {} : { signal }),
  });
  return { items: envelope.data.items, warnings: envelope.meta.warnings };
}

export interface UpdateShopPatch {
  /** `undefined` = 不改；`null` = 清空显示名。 */
  readonly displayName?: string | null;
  /** `undefined` = 不改；`null` = 解除品牌归属。 */
  readonly brandId?: string | null;
  /**
   * task-27：**排序位**（非负整数；服务端是顺序的唯一来源）。
   * `undefined` = 不改（旧调用逐字不变）；非法值由**服务端**拒绝（前端不静默修正）。
   */
  readonly sortOrder?: number;
}

/** `PATCH /v1/shops/:shopId`（仅 admin）：设置显示名 / 品牌归属。 */
export async function apiUpdateShop(
  shopId: string,
  patch: UpdateShopPatch,
  signal?: AbortSignal,
): Promise<ShopAdminRow> {
  const envelope = await request<{ shop: ShopAdminRow }>(`/v1/shops/${encodeURIComponent(shopId)}`, {
    method: "PATCH",
    body: {
      ...(patch.displayName === undefined ? {} : { displayName: patch.displayName }),
      ...(patch.brandId === undefined ? {} : { brandId: patch.brandId }),
      // task-27：排序位；缺省不传 ⇒ 旧调用逐字不变。
      ...(patch.sortOrder === undefined ? {} : { sortOrder: patch.sortOrder }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.shop;
}

// ------------------------------------------------------------ 商品库 / 分类树

/** `GET /v1/products` 的查询参数。 */
export interface ProductsQuery {
  /** 标题 / 货号 模糊搜索。 */
  readonly q?: string;
  /** 分类多选；父类由后端展开为「含子类」。 */
  readonly categoryId?: readonly number[];
  /** 默认 false = 只显示 US 商品。 */
  readonly includeNonUs?: boolean;
  readonly status?: string;
  readonly limit?: number;
  /** 游标 = 上一页最后一个 styleCode。 */
  readonly cursor?: string;
}

export interface ProductsPage {
  readonly items: readonly ProductListItem[];
  readonly hasMore: boolean;
  readonly nextCursor: string | null;
  readonly warnings: readonly ResponseWarning[];
}

/** `GET /v1/products`：商品库列表（货号维度）。 */
export async function apiFetchProducts(query: ProductsQuery = {}, signal?: AbortSignal): Promise<ProductsPage> {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q.trim() !== "") params.set("q", query.q.trim());
  if (query.categoryId !== undefined && query.categoryId.length > 0) {
    params.set("categoryId", query.categoryId.join(","));
  }
  // 默认关：只有显式打开时才传，避免「省略 ≠ false」的歧义。
  if (query.includeNonUs === true) params.set("includeNonUs", "true");
  if (query.status !== undefined && query.status.trim() !== "") params.set("status", query.status.trim());
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined && query.cursor.trim() !== "") params.set("cursor", query.cursor.trim());
  const search = params.toString();
  const envelope = await request<ProductListRecord>(`/v1/products${search === "" ? "" : `?${search}`}`, {
    ...(signal === undefined ? {} : { signal }),
  });
  return {
    items: envelope.data.items,
    hasMore: envelope.data.hasMore,
    nextCursor: envelope.meta.nextCursor,
    warnings: envelope.meta.warnings,
  };
}

/** `GET /v1/products/:styleCode`：货号详情。 */
export async function apiFetchProductDetail(styleCode: string, signal?: AbortSignal): Promise<ProductDetail> {
  const envelope = await request<{ product: ProductDetail }>(`/v1/products/${encodeURIComponent(styleCode)}`, {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.product;
}

/** `GET /v1/categories`：品牌级分类树（含子类商品数 + 未分类档）。 */
export async function apiFetchCategories(
  signal?: AbortSignal,
): Promise<{ readonly tree: CategoryTreeData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<CategoryTreeData>("/v1/categories", {
    ...(signal === undefined ? {} : { signal }),
  });
  return { tree: envelope.data, warnings: envelope.meta.warnings };
}

/** `PATCH /v1/categories/:categoryId`（仅 admin）：**只**改 sortOrder。 */
export async function apiUpdateCategorySort(
  categoryId: number,
  sortOrder: number,
  signal?: AbortSignal,
): Promise<CategoryNode> {
  const envelope = await request<{ category: CategoryNode }>(`/v1/categories/${encodeURIComponent(String(categoryId))}`, {
    method: "PATCH",
    body: { sortOrder },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.category;
}

/**
 * `PATCH /v1/campaign-languages`（仅 admin）：批量设置系列的**语言绑定**。
 *
 * ⚠️ 这是 AMS 自己的「软件自定义属性」（`meta_campaign_language`），
 * **不写回 Meta**、与预算/状态/出价无关。
 *
 * 后端语义（前端不要在这里重实现）：
 *  · 值域只允许 `en` / `de` / `es`，**整批校验**：任一条越界 → 400，一行都不写；
 *  · 结构合法后可能逐条失败（`INVALID_IDENTIFIER` / `DUPLICATE_ITEM`）→ 200 + 逐条结果；
 *  · 非 admin → 403 FORBIDDEN。
 */
export async function apiPatchCampaignLanguages(
  items: readonly CampaignLanguageUpsertItem[],
  signal?: AbortSignal,
): Promise<CampaignLanguageUpsertResult> {
  const envelope = await request<CampaignLanguageUpsertResult>("/v1/campaign-languages", {
    method: "PATCH",
    body: { items },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

export type {
  ActionItemResult,
  ActionPlanResult,
  AdEntityRow,
  CredentialHealth,
  MetricBucket,
  DerivedMetrics,
};
// --------------------------------------------- 「和 FB 同步」异步任务（第十六批 · 用户第 2 条）

/**
 * `POST /v1/sync/trigger`：触发一次同步。
 *
 * `mode`（第二十三批 · 用户第 3 条）：
 *  · `reconcile`（缺省由调用方显式给）= 「核对状态」（轻）：只刷 status/effective_status/预算，不拉 insights；
 *  · `full` = 「全量同步」（重）：实体台账 + 最近 7 天 insights（后端缺省值，**语义未变**）。
 *
 * 立即返回 `jobId`（HTTP 202）；前端随后轮询 `apiFetchSyncTrigger`。
 * 账户选择：传 `shopId`（店铺映射多个账户时后端取第一个并在 warnings 里说明）
 * 或直接传 `accountId`；**两者都不传** → 后端 400 `SHOP_REQUIRED`。
 * 同账户重复点击 → 复用正在跑的任务（`deduped`）；达到并发上限 → 409 `SYNC_BUSY`。
 */
export async function apiTriggerSync(
  body: { readonly shopId?: string; readonly accountId?: string; readonly mode?: "reconcile" | "full" },
  signal?: AbortSignal,
): Promise<{ readonly job: SyncTriggerJob; readonly deduped: boolean; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<{ job: SyncTriggerJob; deduped: boolean }>("/v1/sync/trigger", {
    method: "POST",
    body,
    ...(signal === undefined ? {} : { signal }),
  });
  return { job: envelope.data.job, deduped: envelope.data.deduped === true, warnings: envelope.meta.warnings };
}

/** `GET /v1/sync/trigger/:id`：轮询任务状态（running / succeeded / partial / failed + 进度）。 */
export async function apiFetchSyncTrigger(jobId: string, signal?: AbortSignal): Promise<SyncTriggerJob> {
  const envelope = await request<{ job: SyncTriggerJob }>(`/v1/sync/trigger/${encodeURIComponent(jobId)}`, {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.job;
}

// ------------------------------------------------- 货号分组（第十八批，迁移 0033）

/**
 * `GET /v1/style-code-groups?shopId=`：分组面板（候选货号 + 分组 + 未分组计数）。
 *
 * 候选 = 该店铺当前**有 ACTIVE 广告关联**的货号；已分组但当前没有 ACTIVE 关联的成员
 * 只在 `hiddenMemberCount` 里计数（归属保留、面板不显示）。
 */
export async function apiFetchStyleCodeGroups(
  shopId: string,
  signal?: AbortSignal,
): Promise<StyleCodeGroupPanel> {
  const envelope = await request<StyleCodeGroupPanel>("/v1/style-code-groups", {
    query: { shopId },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** `POST /v1/style-code-groups`：新建分组（admin；重名 → 400）。 */
export async function apiCreateStyleCodeGroup(
  input: { readonly shopId: string; readonly name: string; readonly sortOrder?: number },
  signal?: AbortSignal,
): Promise<StyleCodeGroupWriteRow> {
  const envelope = await request<{ group: StyleCodeGroupWriteRow }>("/v1/style-code-groups", {
    method: "POST",
    body: input,
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.group;
}

/** `PATCH /v1/style-code-groups/:groupId`：改名（admin；组不存在 → 404）。 */
export async function apiRenameStyleCodeGroup(
  groupId: string,
  input: { readonly shopId: string; readonly name: string },
  signal?: AbortSignal,
): Promise<StyleCodeGroupWriteRow> {
  const envelope = await request<{ group: StyleCodeGroupWriteRow }>(
    `/v1/style-code-groups/${encodeURIComponent(groupId)}`,
    { method: "PATCH", body: input, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.group;
}

/** `DELETE /v1/style-code-groups/:groupId?shopId=`：删除分组（成员级联 → 回到未分组）。 */
export async function apiDeleteStyleCodeGroup(
  groupId: string,
  shopId: string,
  signal?: AbortSignal,
): Promise<void> {
  await request<{ deleted: boolean; groupId: string }>(
    `/v1/style-code-groups/${encodeURIComponent(groupId)}`,
    { method: "DELETE", query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
}

/**
 * `POST /v1/style-code-groups/members`：把一批货号**移动**到某个组
 * （`groupId=null` = 移出分组）。语义是**移动不是复制**（后端 `ON CONFLICT DO UPDATE`）。
 */
export async function apiAssignStyleCodes(
  input: { readonly shopId: string; readonly groupId: string | null; readonly styleCodes: readonly string[] },
  signal?: AbortSignal,
): Promise<{ readonly movedCount: number }> {
  const envelope = await request<{ movedCount: number }>("/v1/style-code-groups/members", {
    method: "POST",
    body: input,
    ...(signal === undefined ? {} : { signal }),
  });
  return { movedCount: envelope.data.movedCount };
}

// ------------------------------------- Google 系列层管理（2026-10-05 第二十批）

/** `GET /v1/google-ads/campaigns` 的筛选 / 排序 / 分页参数。 */
export interface GoogleCampaignsQuery {
  readonly shopId?: string;
  readonly status?: "active" | "all" | "removed";
  readonly attribution?: "all" | "matched" | "unmatched";
  /**
   * **投放状态**（2026-10-11 用户第 7 条）：`enabled` 启用中 / `paused` 已暂停。
   *
   * ⚠️ 与上面 `status`（在用/已删除）**是两个维度**：页面两个下拉并列。
   * 不传 = 不筛（默认行为与改造前逐字一致）。
   */
  readonly campaignStatus?: "enabled" | "paused";
  /** **广告类型**（Google 原生枚举，如 `PERFORMANCE_MAX`）；不传 = 不筛。 */
  readonly channelType?: string;
  /**
   * **时间窗口**（2026-10-12 用户截图第 1 条）：业务日 `YYYY-MM-DD`，含两端。
   *
   * 口径（后端 T2a 已上线）：显式窗口占「30 天」那一档（页面主窗口），
   * `from7 = max(from, to-6)`；右端晚于"已结算业务日"会被**钉住**并在响应里披露
   * （`windowToClamped`）。⛔ 只给 `to` ⇒ 400（窗口长度无从得知）⇒ 前端**总是给出 `from`**。
   */
  readonly from?: string;
  readonly to?: string;
  /** **系列名子串搜索**（大小写不敏感），SQL 层过滤。 */
  readonly q?: string;
  readonly sort?: "spend30d" | "spend7d" | "dailyBudget" | "campaignName" | "syncedAt";
  readonly order?: "asc" | "desc";
  readonly limit?: number;
  readonly offset?: number;
}

/** `GET /v1/google-ads/campaigns`：结构快照 × 事实表 × 系列级归属的列表 + 汇总。 */
export async function apiFetchGoogleCampaigns(
  query: GoogleCampaignsQuery,
  signal?: AbortSignal,
): Promise<{ readonly data: GoogleCampaignsData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<GoogleCampaignsData>("/v1/google-ads/campaigns", {
    query: { ...query },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/google-ads/overview`：广告类型 × 时间桶（最近 7 天 / 6 个月 / 逐月）的花费与 ROAS。 */
export async function apiFetchGoogleOverview(
  query: { readonly shopId?: string; readonly attribution?: "all" | "matched" | "unmatched" },
  signal?: AbortSignal,
): Promise<{ readonly data: GoogleOverviewData; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<GoogleOverviewData>("/v1/google-ads/overview", {
    query: { ...query },
    ...(signal === undefined ? {} : { signal }),
  });
  return { data: envelope.data, warnings: envelope.meta.warnings };
}

/** `POST /v1/google-ads/actions`：写路径（dry-run 或真写）。 */
export async function apiPostGoogleAction(
  body: {
    readonly action: "pause" | "enable" | "set_daily_budget";
    readonly targets: readonly { readonly customerId: string; readonly campaignId: string }[];
    readonly dailyBudgetMicros?: number;
    readonly dryRun?: boolean;
    readonly confirm?: boolean;
    readonly acknowledgeSharedBudget?: boolean;
    readonly note?: string;
  },
  signal?: AbortSignal,
): Promise<{ readonly plan: GoogleActionPlan; readonly warnings: readonly ResponseWarning[] }> {
  const envelope = await request<GoogleActionPlan>("/v1/google-ads/actions", {
    method: "POST",
    body,
    ...(signal === undefined ? {} : { signal }),
  });
  return { plan: envelope.data, warnings: envelope.meta.warnings };
}

/** `GET /v1/google-ads/actions/budget-history`：最近 N 次**真实**预算调整（micros）。 */
export async function apiFetchGoogleBudgetHistory(
  query: { readonly customerId: string; readonly campaignId: string; readonly limit?: number },
  signal?: AbortSignal,
): Promise<readonly GoogleBudgetHistoryItem[]> {
  const envelope = await request<{ items: readonly GoogleBudgetHistoryItem[] }>(
    "/v1/google-ads/actions/budget-history",
    { query: { ...query }, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.items;
}

/** `POST /v1/google-ads/sync`：触发「同步系列」（结构快照 + 约 30 天指标）。 */
export async function apiTriggerGoogleSync(
  signal?: AbortSignal,
): Promise<{ readonly job: SyncTriggerJob; readonly deduped: boolean }> {
  const envelope = await request<{ job: SyncTriggerJob; deduped: boolean }>("/v1/google-ads/sync", {
    method: "POST",
    body: {},
    ...(signal === undefined ? {} : { signal }),
  });
  return { job: envelope.data.job, deduped: envelope.data.deduped === true };
}

/** `GET /v1/google-ads/sync/:id`：轮询「同步系列」任务状态。 */
export async function apiFetchGoogleSyncTrigger(jobId: string, signal?: AbortSignal): Promise<SyncTriggerJob> {
  const envelope = await request<{ job: SyncTriggerJob }>(
    `/v1/google-ads/sync/${encodeURIComponent(jobId)}`,
    { ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.job;
}

// ------------------------------------------ 自动启停（2026-10-05 第二十一批）
//
// ⚠️ 客户端只做**读 + 配置写**；**没有**「立即执行自动启停」的方法（本轮明确不做）。
// 所有写方法只改我们自己的配置表（后端不碰 Meta）。

/** `GET /v1/auto-control/templates?shopId=`。 */
export async function apiFetchAutoControlTemplates(
  shopId: string,
  signal?: AbortSignal,
): Promise<readonly AutoControlTemplateRow[]> {
  const envelope = await request<{ shopId: string; templates: readonly AutoControlTemplateRow[] }>(
    "/v1/auto-control/templates",
    { query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.templates;
}

/** `POST /v1/auto-control/templates`：新增模版（admin）。 */
export async function apiCreateAutoControlTemplate(
  input: {
    readonly shopId: string;
    readonly name: string;
    readonly config: AutoControlTemplateConfig;
    readonly currency?: string;
    readonly isDefault?: boolean;
  },
  signal?: AbortSignal,
): Promise<AutoControlTemplateRow> {
  const envelope = await request<{ template: AutoControlTemplateRow }>("/v1/auto-control/templates", {
    method: "POST",
    body: input,
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.template;
}

/** `PATCH /v1/auto-control/templates/:templateId`：改名 / 改阈值 / 设默认（admin）。 */
export async function apiUpdateAutoControlTemplate(
  templateId: string,
  input: {
    readonly shopId: string;
    readonly name?: string;
    readonly config?: AutoControlTemplateConfig;
    readonly isDefault?: boolean;
  },
  signal?: AbortSignal,
): Promise<AutoControlTemplateRow> {
  const envelope = await request<{ template: AutoControlTemplateRow }>(
    `/v1/auto-control/templates/${encodeURIComponent(templateId)}`,
    { method: "PATCH", body: input, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.template;
}

/** `DELETE /v1/auto-control/templates/:templateId?shopId=`：删除（默认模版后端拒绝）。 */
export async function apiDeleteAutoControlTemplate(
  templateId: string,
  shopId: string,
  signal?: AbortSignal,
): Promise<void> {
  await request<{ deleted: boolean; templateId: string }>(
    `/v1/auto-control/templates/${encodeURIComponent(templateId)}`,
    { method: "DELETE", query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
}

/** `GET /v1/auto-control/campaigns?shopId=`：哪些系列开了、绑了哪个模版。 */
export async function apiFetchAutoControlCampaigns(
  shopId: string,
  signal?: AbortSignal,
): Promise<readonly AutoControlCampaignRow[]> {
  const envelope = await request<{ shopId: string; items: readonly AutoControlCampaignRow[] }>(
    "/v1/auto-control/campaigns",
    { query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.items;
}

/**
 * `GET /v1/auto-control/preview`：只读预览（会开启 / 会暂停 / 例外 / 缺失不动作 + reason）。
 * ⚠️ 响应里 `wouldExecute === false`；本函数**不产生任何副作用**。
 */
export async function apiFetchAutoControlPreview(
  query: { readonly shopId: string; readonly campaignId: string; readonly templateId?: string },
  signal?: AbortSignal,
): Promise<AutoControlPreviewData> {
  const envelope = await request<AutoControlPreviewData>("/v1/auto-control/preview", {
    query: { ...query },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-budget/preview`（自动预算设计稿 §5）：按**已绑定**模板逐预算层预演。
 *
 * ⚠️ **只读**：不写库、不写 Meta。`summary` 由**契约** `summarizeAutoBudgetPreview` 在客户端汇总
 * （服务端若也返回，以服务端为准）——跳过行**原样输出、不进总量**（§6 假变化防复发）。
 */
/**
 * ⭐⭐ 服务端在契约行之上**追加**的字段（**模板级**口径；2026-10-09 实测事故后固化）。
 *
 * · `tierLabel`：该行**生效模板**的档位文案（区间 + 该档**真实动作**）。
 *   ⛔ 不能用客户端 `tierTextOf(ratioPpm)` 顶替 —— 那读的是**契约默认阶梯**，
 *   模板改过系数（如顶层 130% → 120%）后界面就会说"日预算 130%"而实际按 120% 算（显示 ≠ 执行）。
 * · `bandMinCents` / `bandMaxCents`：生效预算带（`[max(模板MINI, 币种最低日预算), 模板MAX]`）。
 * · `budgetOutOfBand`：**当前预算在带外** ⇒ 本次变化是"被夹回带内"（可能巨幅）⇒ UI 必须标红。
 *
 * ⚠️ 全部可选：老后端/既有夹具不提供时不破（缺省 ⇒ 该格按「—」呈现，⛔ 不猜、不折 0）。
 */
export type AutoBudgetPreviewRowFromApi = AutoBudgetPreviewRow & {
  readonly tierLabel?: string | null | undefined;
  readonly bandMinCents?: number | null | undefined;
  readonly bandMaxCents?: number | null | undefined;
  readonly budgetOutOfBand?: boolean | undefined;
  /**
   * ⭐⭐ 2026-10-15（用户裁定：「没到间隔 ⇒ 显示**计划调整的金额**，以及爬坡要**乘以多少**」）：
   * 计划值那层的三个字段**都由后端算好**（前端只展示、⛔ 不做乘法）：
   *  · `plannedWithRampCents` = 计划值**含爬坡系数**（= 契约 `resolveTargetRampOverlay` 的 `finalCents`，
   *    与执行侧同一个纯函数）；没有爬坡系数 ⇒ 等于契约的 `plannedNewBudgetCents`；
   *  · `rampStepPpm` = 本次采用的爬坡**步长**（`null` = 该层这一轮没有爬坡决策 ⇒ 不给系数，
   *    面板只显示档位那一层）；
   *  · `rampOverlayCents` = 该层当前爬坡决策的**叠加量**（状态①"两来源分开标"用；`null` = 没有）。
   * ⚠️ 全部可选：老后端不提供 ⇒ 退回"只有档位那层 / 没有第二来源"，⛔ 不猜、⛔ 不折 0。
   */
  readonly plannedNewBudgetCents?: number | null | undefined;
  readonly plannedFactorPpm?: number | null | undefined;
  readonly plannedTierId?: string | null | undefined;
  readonly nextAdjustAtIso?: string | null | undefined;
  readonly plannedWithRampCents?: number | null | undefined;
  readonly rampStepPpm?: number | null | undefined;
  readonly rampOverlayCents?: number | null | undefined;
  /**
   * ⭐ 2026-10-15（Lead 裁定）：该行账户的 IANA 时区（`meta_accounts.timezone`）。
   * 「下次调整时间」必须按它显示（⛔ 不打 UTC 让用户自己换算、⛔ 不用浏览器本地时区）。
   * `null`/缺省 ⇒ 面板如实说"未取到账户时区"（⛔ 不编一个时区）。
   */
  readonly accountTimeZone?: string | null | undefined;
};

export interface AutoBudgetPreviewData {
  readonly rows: readonly AutoBudgetPreviewRowFromApi[];
  /** 未绑定模板的系列数（明确显示「未绑定，不会执行」，不静默忽略）。 */
  readonly unboundCampaigns?: number;
}

export async function apiFetchAutoBudgetPreview(
  query: { readonly shopId?: string; readonly campaignId?: string },
  signal?: AbortSignal,
): Promise<AutoBudgetPreviewData> {
  const envelope = await request<AutoBudgetPreviewData>("/v1/auto-budget/preview", {
    query: { ...query },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-control/runs`：最近执行记录 + 跳过原因统计 + 执行开关状态（第二十三批）。
 * 只读；HTTP 层不做执行（执行由 worker 定时任务完成）。
 */
export async function apiFetchAutoControlRuns(
  input: { readonly shopId: string; readonly campaignId?: string; readonly runId?: string; readonly limit?: number },
  signal?: AbortSignal,
): Promise<AutoControlRunsData> {
  const envelope = await request<AutoControlRunsData>("/v1/auto-control/runs", {
    query: { ...input },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-control/executions`（2026-10-06 · task-4）：真执行次数 + 执行记录明细。
 *
 * 只读：不触发执行、不写 Meta。指标来自该业务日 ad 事实（事后物化值；缺行 = 未记录）。
 */
export async function apiFetchAutoControlExecutions(
  input: {
    readonly shopId: string;
    readonly campaignId?: string;
    readonly adId?: string;
    readonly limit?: number;
  },
  signal?: AbortSignal,
): Promise<AutoControlExecutionsData> {
  const envelope = await request<AutoControlExecutionsData>("/v1/auto-control/executions", {
    query: { ...input },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `POST /v1/auto-control/runs/rollback`（P2 任务 3）：按 runId 回滚自动启停。
 *
 * ⚠️ 复用既有 `POST /v1/actions` 的**同一执行器**：`dryRun:true` 只预演（返回反向计划）；
 * 真写必须 `dryRun:false` **且** `confirm:true` **且** 服务端 `AMS_ALLOW_META_WRITES=1`。
 * 同一 runId 重复回滚由服务端幂等保护（`alreadyRolledBack`）。
 */
export async function apiPostAutoControlRollback(
  input: {
    readonly shopId: string;
    readonly runId: string;
    readonly campaignId?: string | null;
    readonly dryRun: boolean;
    readonly confirm: boolean;
    readonly note?: string;
  },
  signal?: AbortSignal,
): Promise<AutoControlRollbackData> {
  const envelope = await request<AutoControlRollbackData>("/v1/auto-control/runs/rollback", {
    method: "POST",
    body: {
      shopId: input.shopId,
      runId: input.runId,
      ...(input.campaignId === undefined || input.campaignId === null ? {} : { campaignId: input.campaignId }),
      dryRun: input.dryRun,
      confirm: input.confirm,
      ...(input.note === undefined ? {} : { note: input.note }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/meta-pending?shopId=`（2026-10-05 · P2 收尾）：
 * 顶部「待处理 / 未完成」区域 + 广告树那一行 / 编辑面板的 pending 徽章**同一数据源**。
 *
 * `status` 缺省由服务端给 `pending,failed`（成功的收敛不需要打扰用户）。
 */
export async function apiFetchMetaPending(
  input: { readonly shopId: string; readonly status?: string; readonly limit?: number },
  signal?: AbortSignal,
): Promise<MetaPendingData> {
  const envelope = await request<MetaPendingData>("/v1/meta-pending", {
    query: {
      shopId: input.shopId,
      ...(input.status === undefined ? {} : { status: input.status }),
      ...(input.limit === undefined ? {} : { limit: input.limit }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** `POST /v1/meta-pending/:pendingId/retry`：一键重试（原动作 + 原目标）。 */
export async function apiPostPendingRetry(
  input: { readonly pendingId: string; readonly dryRun: boolean; readonly confirm: boolean; readonly note?: string },
  signal?: AbortSignal,
): Promise<PendingRetryData> {
  const envelope = await request<PendingRetryData>(`/v1/meta-pending/${encodeURIComponent(input.pendingId)}/retry`, {
    method: "POST",
    body: {
      dryRun: input.dryRun,
      confirm: input.confirm,
      ...(input.note === undefined ? {} : { note: input.note }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** `POST /v1/meta-pending/:pendingId/rollback`：一键回滚（反向动作 + before）。 */
export async function apiPostPendingRollback(
  input: { readonly pendingId: string; readonly dryRun: boolean; readonly confirm: boolean; readonly note?: string },
  signal?: AbortSignal,
): Promise<PendingRollbackData> {
  const envelope = await request<PendingRollbackData>(`/v1/meta-pending/${encodeURIComponent(input.pendingId)}/rollback`, {
    method: "POST",
    body: {
      dryRun: input.dryRun,
      confirm: input.confirm,
      ...(input.note === undefined ? {} : { note: input.note }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `PUT /v1/auto-control/campaigns/:campaignId`：开关自动启停 / 绑定模版。
 * ⚠️ **只写配置**：本轮不会因此真的启停任何广告（真执行由 worker 定时任务按开关进行）。
 */
export async function apiUpsertAutoControlCampaign(
  campaignId: string,
  input: {
    readonly shopId: string;
    readonly enabled: boolean;
    readonly templateId?: string | null;
    readonly accountId?: string | null;
  },
  signal?: AbortSignal,
): Promise<AutoControlCampaignRow> {
  const envelope = await request<{ campaign: AutoControlCampaignRow }>(
    `/v1/auto-control/campaigns/${encodeURIComponent(campaignId)}`,
    { method: "PUT", body: input, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.campaign;
}

/** `POST /v1/auto-control/exceptions`：加入例外（快照由服务端取）。 */
export async function apiAddAutoControlException(
  input: { readonly shopId: string; readonly campaignId: string; readonly adId: string },
  signal?: AbortSignal,
): Promise<AutoControlExceptionRow> {
  const envelope = await request<{ exception: AutoControlExceptionRow }>("/v1/auto-control/exceptions", {
    method: "POST",
    body: input,
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.exception;
}

/** `DELETE /v1/auto-control/exceptions?shopId=&campaignId=&adId=`：移除例外。 */
export async function apiRemoveAutoControlException(
  input: { readonly shopId: string; readonly campaignId: string; readonly adId: string },
  signal?: AbortSignal,
): Promise<void> {
  await request<{ deleted: boolean; adId: string }>("/v1/auto-control/exceptions", {
    method: "DELETE",
    query: { ...input },
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `GET /v1/shop-budget-ratios`：按店铺的广告日预算比例配置（`null` = 未配置）。 */
/**
 * `GET /v1/compliance/audit`（task-19 · 只读）：合规体检清单 + 按类别汇总。
 *
 * 缺省不传 `shopId`/`brandId` = 全品牌（与页面右上角全局筛选一致，由调用方决定）。
 * 不做任何本地换算：服务端给什么就是什么（空数组 = 空数组，绝不补 0）。
 */
export async function apiFetchComplianceAudit(
  query: {
    readonly shopId?: string;
    readonly brandId?: string;
    readonly category?: string;
    readonly limit?: number;
    readonly offset?: number;
  } = {},
  signal?: AbortSignal,
): Promise<ComplianceAuditData> {
  const envelope = await request<ComplianceAuditData>("/v1/compliance/audit", {
    query: {
      ...(query.shopId === undefined ? {} : { shopId: query.shopId }),
      ...(query.brandId === undefined ? {} : { brandId: query.brandId }),
      ...(query.category === undefined ? {} : { category: query.category }),
      ...(query.limit === undefined ? {} : { limit: query.limit }),
      ...(query.offset === undefined ? {} : { offset: query.offset }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

export async function apiFetchShopBudgetRatios(signal?: AbortSignal): Promise<ShopBudgetRatioListData> {
  const envelope = await request<ShopBudgetRatioListData>("/v1/shop-budget-ratios", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `PUT /v1/shop-budget-ratios/:shopId`：写入某店铺的 Meta% / Google%（ppm 整数，`null` = 未配置）。
 *
 * 服务端校验非法值并**拒绝**（不是静默修正）；审计由服务端写 `operation_log`。
 * 两个比例是**各自占销售额**的独立比例，**不是**两者瓜分广告预算（不归一化）。
 */
export async function apiPutShopBudgetRatio(
  input: {
    readonly shopId: string;
    readonly metaSharePpm: number | null;
    readonly googleSharePpm: number | null;
  },
  signal?: AbortSignal,
): Promise<ShopBudgetRatioWriteData> {
  const envelope = await request<ShopBudgetRatioWriteData>(
    `/v1/shop-budget-ratios/${encodeURIComponent(input.shopId)}`,
    {
      method: "PUT",
      body: { metaSharePpm: input.metaSharePpm, googleSharePpm: input.googleSharePpm },
      ...(signal === undefined ? {} : { signal }),
    },
  );
  return envelope.data;
}

// ------------------------------------------------ RBAC ⑥ 角色管理（2026-10-08）

/**
 * `GET /v1/roles`：**全局**角色列表（角色不含店铺）。
 * ⚠️ 前端**不重算**有效档位：`/v1/auth/me` 的 `featureAccess` 已给 `{access, source}`。
 */
export async function apiFetchRoles(signal?: AbortSignal): Promise<readonly RoleAdminRecord[]> {
  const envelope = await request<{ items: readonly RoleAdminRecord[] }>("/v1/roles", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data.items;
}

/** `POST /v1/roles`（201）⇒ 新建自定义角色（`{name, grants}`）。 */
export async function apiCreateRole(input: {
  readonly name: string;
  readonly grants: readonly RoleGrantInput[];
}): Promise<RoleAdminRecord> {
  const envelope = await request<{ role: RoleAdminRecord }>("/v1/roles", {
    method: "POST",
    body: input,
  });
  return envelope.data.role;
}

/** `PATCH /v1/roles/:roleId` ⇒ 改名 / 改档位（内置角色会被服务端以 `ROLE_BUILTIN_IMMUTABLE` 拒绝）。 */
export async function apiUpdateRole(
  roleId: string,
  input: { readonly name: string; readonly grants: readonly RoleGrantInput[] },
): Promise<RoleAdminRecord> {
  const envelope = await request<{ role: RoleAdminRecord }>(
    `/v1/roles/${encodeURIComponent(roleId)}`,
    { method: "PATCH", body: input },
  );
  return envelope.data.role;
}

/** `PUT /v1/roles/:roleId/grants` ⇒ **整组替换**档位（不是增量）。 */
export async function apiPutRoleGrants(
  roleId: string,
  input: { readonly grants: readonly RoleGrantInput[] },
): Promise<RoleAdminRecord> {
  const envelope = await request<{ role: RoleAdminRecord }>(
    `/v1/roles/${encodeURIComponent(roleId)}/grants`,
    { method: "PUT", body: input },
  );
  return envelope.data.role;
}

/**
 * `DELETE /v1/roles/:roleId`。被引用 ⇒ **409 `ROLE_IN_USE`**，`details` 里**逐个列出**仍在用该角色的用户
 * ⇒ 调用方必须把清单**逐条显示**并提示"先改派"（**不要**吞成"保存失败"）。
 */
export async function apiDeleteRole(roleId: string): Promise<{ readonly roleId: string; readonly deleted: true }> {
  const envelope = await request<{ roleId: string; deleted: true }>(
    `/v1/roles/${encodeURIComponent(roleId)}`,
    { method: "DELETE" },
  );
  return envelope.data;
}

// ------------------------------------------- 自动预算「模板管理」（2026-10-08）

/**
 * `GET /v1/auto-budget/templates?shopId=…`：当前店铺的模板列表。
 * （此前前端只用了 `apiFetchAutoBudgetPreview` ⇒ 用户找不到模板管理，因为**界面不存在**。）
 */
export async function apiFetchAutoBudgetTemplates(
  shopId: string,
  signal?: AbortSignal,
): Promise<readonly AutoBudgetTemplateRecord[]> {
  const envelope = await request<{ templates: readonly AutoBudgetTemplateRecord[] }>(
    "/v1/auto-budget/templates",
    { query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.templates;
}

/** `POST /v1/auto-budget/templates`（201）：`name` / `config` **原样透传**（合法性由 store 过契约判定）。 */
export async function apiCreateAutoBudgetTemplate(input: {
  readonly shopId: string;
  readonly name: string;
  readonly config: AutoBudgetTemplateConfig;
}): Promise<AutoBudgetTemplateRecord> {
  const envelope = await request<{ template: AutoBudgetTemplateRecord }>("/v1/auto-budget/templates", {
    method: "POST",
    // ⭐ `shopId` **走 query**（端点用 `requireShopId(url.searchParams)`）⇒ 放 body 会**必然 400**。
    query: { shopId: input.shopId },
    body: { name: input.name, config: input.config },
  });
  return envelope.data.template;
}

/** `PATCH /v1/auto-budget/templates/:templateId`：改名 / 改配置（`INVALID_ARGUMENT` ⇒ 逐条 `details`）。 */
export async function apiUpdateAutoBudgetTemplate(
  templateId: string,
  input: { readonly shopId: string; readonly name: string; readonly config: AutoBudgetTemplateConfig },
): Promise<AutoBudgetTemplateRecord> {
  const envelope = await request<{ template: AutoBudgetTemplateRecord }>(
    `/v1/auto-budget/templates/${encodeURIComponent(templateId)}`,
    { method: "PATCH", query: { shopId: input.shopId }, body: { name: input.name, config: input.config } },
  );
  return envelope.data.template;
}

/**
 * `DELETE /v1/auto-budget/templates/:templateId`。
 * 被引用 ⇒ **409 `TEMPLATE_IN_USE`**，`details` 里逐个列出仍在用的系列
 * ⇒ 调用方必须**逐条显示**并提示「先解绑」（不要吞成"删除失败"）。
 */
export async function apiDeleteAutoBudgetTemplate(templateId: string, shopId: string): Promise<void> {
  await request<unknown>(`/v1/auto-budget/templates/${encodeURIComponent(templateId)}`, {
    method: "DELETE",
    query: { shopId },
  });
}

/**
 * `PUT /v1/auto-budget/campaigns/:campaignId/binding`：把模板绑到系列（`templateId: null` ⇒ **解绑**）。
 * ⚠️ 响应形状按"绑定后的系列视图"透传（本函数不解释它，只回 `envelope.data`）。
 */
export async function apiSetAutoBudgetCampaignBinding(
  campaignId: string,
  /**
   * ⚠️ **PUT 是整体替换**（不是 PATCH）⇒ **`enabled` 必填布尔**：
   *  · 非布尔 ⇒ **400 `not_a_boolean`**（只传 `templateId` 会被拒 —— 这正是此前绑定/解绑全部 400 的原因）；
   *  · `templateId: null` ⇒ 解绑，且 `enabled` **必须 false**（服务端拒 `enabled_without_template`）。
   */
  input: { readonly shopId: string; readonly templateId: string | null; readonly enabled: boolean },
): Promise<unknown> {
  const envelope = await request<unknown>(
    `/v1/auto-budget/campaigns/${encodeURIComponent(campaignId)}/binding`,
    {
      method: "PUT",
      query: { shopId: input.shopId },
      body: { templateId: input.templateId, enabled: input.enabled },
    },
  );
  return envelope.data;
}

/**
 * `GET /v1/auto-budget/bindings?shopId=…`：该店铺的**绑定快照**（每系列一条，已 upsert ⇒ 无历史/软删行）。
 *
 * 用途：模板列表里的**被引用数**（客户端按 `templateId` 计数）。
 * ⚠️ 载荷数组字段名是 **`bindings`**（不是 `items`）。
 */
export async function apiFetchAutoBudgetBindings(
  shopId: string,
  signal?: AbortSignal,
): Promise<readonly AutoBudgetBindingRecord[]> {
  const envelope = await request<{ shopId: string; bindings: readonly AutoBudgetBindingRecord[] }>(
    "/v1/auto-budget/bindings",
    { query: { shopId }, ...(signal === undefined ? {} : { signal }) },
  );
  return envelope.data.bindings;
}

/**
 * `GET /v1/meta/audience-geo`（2026-10-10 · 用户选「A」）：**按需**读该系列的受众国家。
 *
 * ⚠️ 这是**唯一**会为了"看定位"而打 Meta 的读取 ⇒ 只在用户点击时调用（后端 5 分钟缓存）。
 */
export async function apiFetchAudienceGeo(
  query: { readonly accountId: string; readonly campaignId: string },
  signal?: AbortSignal,
): Promise<AudienceGeoData> {
  const envelope = await request<AudienceGeoData>("/v1/meta/audience-geo", {
    query: { accountId: query.accountId, campaignId: query.campaignId },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

// ---------------------------------------------------------------------------
// 纳新规则（2026-10-10 · 用户需求）
//
// ⚠️ 所有调用都带 `shopId` **query**：端点的权限闸门（`shopParams: ["shopId"]`）靠它校验店铺可见性
// —— 少了这个参数，服务端会直接 400（这是有意的，⛔ 不是"顺手加的参数"）。
// ---------------------------------------------------------------------------

const enrollmentBase = (kind: "auto_control" | "auto_budget"): string =>
  kind === "auto_control" ? "/v1/auto-control/enrollment" : "/v1/auto-budget/enrollment";

export async function apiFetchEnrollmentRules(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly signal?: AbortSignal;
}): Promise<readonly EnrollmentRuleView[]> {
  const envelope = await request<{ kind: string; rules: readonly EnrollmentRuleView[] }>(
    `${enrollmentBase(input.kind)}/rules`,
    { query: { shopId: input.shopId }, ...(input.signal === undefined ? {} : { signal: input.signal }) },
  );
  return envelope.data.rules;
}

export async function apiCreateEnrollmentRule(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly templateId: string;
  readonly startsOn: string;
  readonly allowBackfill: boolean;
  readonly keywords: readonly string[];
  readonly excludeKeywords: readonly string[];
  readonly protectionDays: number;
  readonly enabled: boolean;
}): Promise<EnrollmentRuleView> {
  const envelope = await request<{ rule: EnrollmentRuleView }>(`${enrollmentBase(input.kind)}/rules`, {
    method: "POST",
    query: { shopId: input.shopId },
    body: {
      templateId: input.templateId,
      startsOn: input.startsOn,
      allowBackfill: input.allowBackfill,
      keywords: [...input.keywords],
      excludeKeywords: [...input.excludeKeywords],
      protectionDays: input.protectionDays,
      enabled: input.enabled,
    },
  });
  return envelope.data.rule;
}

export async function apiUpdateEnrollmentRule(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly ruleId: string;
  readonly patch: Readonly<Record<string, unknown>>;
}): Promise<EnrollmentRuleView> {
  const envelope = await request<{ rule: EnrollmentRuleView }>(
    `${enrollmentBase(input.kind)}/rules/${encodeURIComponent(input.ruleId)}`,
    { method: "PATCH", query: { shopId: input.shopId }, body: input.patch },
  );
  return envelope.data.rule;
}

export async function apiDeleteEnrollmentRule(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly ruleId: string;
}): Promise<{ readonly deleted: boolean; readonly enrolledCount: number }> {
  const envelope = await request<{ deleted: boolean; enrolledCount: number }>(
    `${enrollmentBase(input.kind)}/rules/${encodeURIComponent(input.ruleId)}`,
    { method: "DELETE", query: { shopId: input.shopId } },
  );
  return envelope.data;
}

export async function apiPreviewEnrollmentRule(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly ruleId: string;
  readonly signal?: AbortSignal;
}): Promise<EnrollmentPreviewData> {
  const envelope = await request<EnrollmentPreviewData>(
    `${enrollmentBase(input.kind)}/rules/${encodeURIComponent(input.ruleId)}/preview`,
    { query: { shopId: input.shopId }, ...(input.signal === undefined ? {} : { signal: input.signal }) },
  );
  return envelope.data;
}

export async function apiExitEnrollment(input: {
  readonly kind: "auto_control" | "auto_budget";
  readonly shopId: string;
  readonly ruleId: string;
  readonly campaignId?: string;
}): Promise<unknown> {
  const envelope = await request<unknown>(
    `${enrollmentBase(input.kind)}/rules/${encodeURIComponent(input.ruleId)}/exit`,
    {
      method: "POST",
      query: { shopId: input.shopId },
      body: input.campaignId === undefined ? {} : { campaignId: input.campaignId },
    },
  );
  return envelope.data;
}

// ---------------------------------------------------------------------------
// 目标 ROAS 系数爬坡（影子）· 2026-10-11 用户裁定「先做影子、先不执行」
//
// ⚠️ 这条分隔线有**结构性作用**：纳新守卫（`EnrollmentRulePanel.spec.ts`）取
//    `const enrollmentBase` 到**下一条分隔线**之间的块，断言"每个请求都带
//    `query: { shopId: input.shopId }`"。本节的调用不属于纳新（GET 直接收 `shopId`），
//    必须落在分隔线**之外**（否则守卫会把它算成纳新请求而误报）。
// ─────────────────────────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// 目标爬坡（2026-10-11 用户裁定）—— ⚠️ 这条分隔线是**结构性**的：
// `EnrollmentRulePanel.spec.ts` 的纳新守卫按分隔线切块，缺了它新函数会被算进纳新块 ⇒ 守卫红（实测踩过）。
// ---------------------------------------------------------------------------
/**
 * `GET /v1/auto-budget/target-ramp/shadow?shopId=`（⭐ 2026-10-11 用户裁定「先做影子、先不执行」）。
 *
 * ⚠️ **只读**：这个端点只返回"若生效会怎么调"的对照表，不会改动任何预算
 * （影子趟里没有预算写入路径，由后端源码级断言钉住）。
 */
export async function apiFetchTargetRampShadow(
  shopId: string,
  signal?: AbortSignal,
): Promise<TargetRampShadowData> {
  const envelope = await request<TargetRampShadowData>("/v1/auto-budget/target-ramp/shadow", {
    query: { shopId },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `PUT /v1/auto-budget/target-ramp/target`：保存**店铺级** 30 天目标 ROAS。
 *
 * `shopId` **同时**放 query 与 body：query 供服务端权限闸门（`shopParams`）判定，
 * body 与其它写接口的形状保持一致（⛔ 两处都不许省）。
 */
export async function apiSaveTargetRampTarget(input: {
  readonly shopId: string;
  readonly targetRoas: number;
  readonly effectiveFrom: string;
}): Promise<TargetRampTargetRecord> {
  const envelope = await request<{ target: TargetRampTargetRecord }>("/v1/auto-budget/target-ramp/target", {
    method: "PUT",
    query: { shopId: input.shopId },
    body: { shopId: input.shopId, targetRoas: input.targetRoas, effectiveFrom: input.effectiveFrom },
  });
  return envelope.data.target;
}

/**
 * `POST /v1/auto-budget/target-ramp/shadow/run?shopId=` —— **立即重算一轮影子**。
 *
 * ⚠️ 用户上次的困惑（「设了目标为什么没反应」）就是因为"设目标不触发计算"：
 * 判定只由影子趟产出（定时 02:30 / 08:30 / 14:30 / 20:30）。这个端点让用户**当场**看到结果。
 *
 * ⛔ 它**只算不写**：与定时影子趟同一条链路（端口形状里没有 Meta 写端口 + 源码级断言钉住）
 * ⇒ 按钮旁必须写清"不会改动任何预算"。
 * `shopId` 同时放 query 与 body（query 供权限闸门，body 与其它写接口形状一致 ⇒ 两处都不省）。
 */
export async function apiRunTargetRampShadow(shopId: string): Promise<TargetRampShadowRunResult> {
  const envelope = await request<TargetRampShadowRunResult>("/v1/auto-budget/target-ramp/shadow/run", {
    method: "POST",
    query: { shopId },
    body: { shopId },
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-budget/target-ramp/steps?shopId=&limit=` —— **最近执行**的爬坡步骤。
 *
 * 正式执行上线前该表为空 ⇒ 展示层给空态说明（⛔ 不显示假数据、⛔ 不显示 $0.00）。
 */
export async function apiFetchTargetRampSteps(
  shopId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<TargetRampStepsData> {
  const envelope = await request<TargetRampStepsData>("/v1/auto-budget/target-ramp/steps", {
    query: { shopId, limit: String(limit) },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

// --------------------------------------------------------------------- 信封
// 自动并入 · 目标配置（只读；⛔ 该端点不写预算/状态、⛔ 不绑模板）
//
// ⚠️ 这条分隔线是**结构性**的：纳新守卫按分隔线切块 ⇒ 新函数必须落在自己的块里。
// --------------------------------------------------------------------- 信封
/**
 * `GET /v1/auto-merge/targets?shopId=` —— 自动并入的目标配置（**只读**）。
 * 缺失字段一律 `null`（前端显示 `—`，⛔ 不折 0）；`source === null` ⇒ 源未配置。
 */
export async function apiFetchAutoMergeTargets(
  shopId: string,
  /**
   * ⭐ 2026-10-15：店铺级**排除关键字**（用户自己填；逗号分隔上传）。
   * 空数组 ⇒ **不加该 query 参数**（= 不过滤，与后端缺省逐字等价 ✓）。
   */
  excludeKeywords: readonly string[] = [],
  signal?: AbortSignal,
): Promise<AutoMergeTargetsData> {
  const envelope = await request<AutoMergeTargetsData>("/v1/auto-merge/targets", {
    query: excludeKeywords.length === 0
      ? { shopId }
      : { shopId, excludeKeywords: excludeKeywords.join(",") },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-merge/source-campaigns?shopId=&comboKey=` —— **最终作为源的广告系列**列表（**只读**）。
 *
 * ⚠️ `comboKey` 必填（后端缺 ⇒ 400，⛔ 不猜一个组）；店铺级排除关键字一并传（判定"是不是源"要用它 ✓）。
 */
/**
 * `GET /v1/credentials/meta-tokens` —— Meta 令牌状态（**⛔ 响应里没有完整令牌**，只有尾号）。
 * 权限：功能点 `settings_credentials:read`（**可授予任意角色/用户**，⛔ 不是超管专属）。
 */
export async function apiFetchMetaTokenStatus(signal?: AbortSignal): Promise<MetaTokenOverview> {
  const envelope = await request<MetaTokenOverview>("/v1/credentials/meta-tokens", {
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `POST /v1/credentials/meta-tokens` —— **回填**令牌（验证 → 换长期 → 写 600 文件 → 热更新）。
 *
 * ⚠️ 令牌只在**请求体**里（⛔ URL/查询串）；前端**不缓存、不落 localStorage、不回显**。
 * 权限：`settings_credentials:write`。
 */
export async function apiUpdateMetaToken(input: {
  readonly which: "user" | "system_user";
  readonly token: string;
}): Promise<MetaTokenUpdateResult> {
  const envelope = await request<MetaTokenUpdateResult>("/v1/credentials/meta-tokens", {
    method: "POST",
    body: input,
  });
  return envelope.data;
}

export async function apiFetchAutoMergeSourceCampaigns(
  input: { readonly shopId: string; readonly comboKey: string; readonly excludeKeywords?: readonly string[] },
  signal?: AbortSignal,
): Promise<AutoMergeSourceCampaignsData> {
  const keywords = input.excludeKeywords ?? [];
  const envelope = await request<AutoMergeSourceCampaignsData>("/v1/auto-merge/source-campaigns", {
    query: {
      shopId: input.shopId,
      comboKey: input.comboKey,
      ...(keywords.length === 0 ? {} : { excludeKeywords: keywords.join(",") }),
    },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `GET /v1/auto-merge/combo-shares?shopId=` —— 占比明细（**只读**；本轮为**近似**口径）。
 *
 * ⚠️ 排除关键字**一并传**（否则"组列表排掉的系列还在占比分母里"✗）；空数组 ⇒ 不加参数。
 */
export async function apiFetchAutoMergeComboShares(
  shopId: string,
  excludeKeywords: readonly string[] = [],
  signal?: AbortSignal,
): Promise<AutoMergeComboSharesData> {
  const envelope = await request<AutoMergeComboSharesData>("/v1/auto-merge/combo-shares", {
    query: excludeKeywords.length === 0
      ? { shopId }
      : { shopId, excludeKeywords: excludeKeywords.join(",") },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

// ------------------------------------------------------------------------------ 自动并入 · 设置
// 写路径；2026-10-13 · 用户裁定「先做第一步：确定源系列与目标系列」
//
// ⚠️ 三条**只写配置表**：⛔ 不写 Meta ✗、⛔ 不改预算 ✗、⛔ 不绑模板 ✗、⛔ 不暂停任何广告 ✗。
//    「源 = 绑某自动预算模板」在设置页只是**记录**；真去绑模板是另一个动作（影响钱 ⇒ 需用户单独点头 ✓）。

/** `PUT /v1/auto-merge/default-locales?shopId=` 的 `data`。 */
export interface AutoMergeDefaultLocalesSaved {
  readonly shopId: string;
  /** 写后回读：`null` = 未配置 ✓；`[]` = 明确无默认 ✓（三态不与"未配置"混同）。 */
  readonly locales: readonly string[] | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  /** 恒为 `false` ✓ —— 这条只写系统内配置，绝不写 Meta（界面据此显示说明）。 */
  readonly writesMeta: boolean;
}

/**
 * `PUT /v1/auto-merge/default-locales?shopId=` —— 站点默认语言（**只在系统内生效** ✓）。
 *
 * ⚠️ 三态：`locales: null` ⇒ 未配置 ✓；`[]` ⇒ 明确"无默认"（异常态）✓；非空 ⇒ 默认语言 ✓。
 * ⛔ 不写 Meta ✗ —— 在 Meta 里设语言会**真的限制投放人群** ⇒ 影响钱 ✗。
 */
export async function apiSaveAutoMergeDefaultLocales(
  shopId: string,
  locales: readonly string[] | null,
  signal?: AbortSignal,
): Promise<AutoMergeDefaultLocalesSaved> {
  const envelope = await request<AutoMergeDefaultLocalesSaved>("/v1/auto-merge/default-locales", {
    method: "PUT",
    query: { shopId },
    body: { locales },
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/** `PUT /v1/auto-merge/source-config?shopId=&comboKey=` 的 `data`。 */
export interface AutoMergeSourceConfigSaved {
  readonly shopId: string;
  readonly comboKey: string;
  readonly source: AutoMergeSourceData | null;
  readonly updatedBy: string | null;
  /** 恒为 `false` ✓ —— 保存配置**不会**去绑模板（绑模板是另一个动作 ⇒ 钱相关 ⇒ 需单独确认 ✓）。 */
  readonly templateBindingApplied: boolean;
  /** 恒为 `false` ✓ —— 只写系统内配置。 */
  readonly writesMeta: boolean;
}

/**
 * `PUT /v1/auto-merge/source-config?shopId=&comboKey=` —— 源与排除词（**只记录**；⛔ 不绑模板 ✗）。
 *
 * ⚠️ 用户裁定：模板 + 排除词是**每个国家语言组自己的** ✓ ⇒ `comboKey` **必填**
 * （少了它就会整店覆盖 ⇒ 那正是用户批评的「不能切国家语言组」✗）。
 */
export async function apiSaveAutoMergeSourceConfig(
  shopId: string,
  comboKey: string,
  input: AutoMergeSourceSaveInput,
  signal?: AbortSignal,
): Promise<AutoMergeSourceConfigSaved> {
  const envelope = await request<AutoMergeSourceConfigSaved>("/v1/auto-merge/source-config", {
    method: "PUT",
    query: { shopId, comboKey },
    body: input,
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}

/**
 * `PUT /v1/auto-merge/targets?shopId=&comboKey=` —— 保存**某一个国家语言组**的主力 + 次主力 ✓。
 *
 * 载荷只报选中的 `entityId`（`null` = 该角色不配 ✓）：账户/组合/花费由**服务端**从该组候选解析
 * （⛔ 不让前端把花费传上来 ✗ —— 配置会与事实漂移）。
 * ⚠️ 服务端校验：① 两个角色不能是同一个广告组 ✓；② 用户写死的「**目标是目标 ⇒ 就不再是源**」
 * ⇒ 冲突时 **400 + 逐条 details**（前端必须逐条显示 ✓，⛔ 不许静默忽略 ✗）；
 * ③ ⚠️ **只动该组**（切组保存不会误删别组 ✓）。
 */
export async function apiSaveAutoMergeTargets(
  shopId: string,
  comboKey: string,
  picks: AutoMergeTargetSaveInput,
  signal?: AbortSignal,
): Promise<AutoMergeSaveResult> {
  const envelope = await request<AutoMergeSaveResult>("/v1/auto-merge/targets", {
    method: "PUT",
    query: { shopId, comboKey },
    body: picks,
    ...(signal === undefined ? {} : { signal }),
  });
  return envelope.data;
}
